import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Link as RouterLink} from "react-router-dom";
import * as THREE from "three";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import ButtonAdd from "../../../components/UI/button/ButtonAdd";
import ButtonExit from "../../../components/UI/button/ButtonExit";
import {ModuleCompositionWidthConfirmDialogContent} from "../../../components/composition/ModuleCompositionWidthConfirmDialogContent";
import {focusModuleWidthInput} from "../../../components/composition/CompositionMediaView";
import {CATALOG_MODULES_VIEW_TOGGLE_OVERLAY_RESERVE_PX} from "../../../components/composition/CatalogModulesViewModeToggle";
import CompositionController from "../../../API/organization/CompositionController";
import CustomerCompositionController from "../../../API/customer/CustomerCompositionController";
import {buildModuleCompositionWidthPatchPayload} from "../../../utils/mapModuleCompositionToModuleItem";
import {
    applyCustomer3dSceneCompositionPatch,
    buildCatalogModuleWidthPatchRequest,
    getModuleCompositionDisplayName,
    getModuleRowsFromCompositionPayload,
    collectModuleCompositionRowIdentityKeys,
    restoreCustomer3dSceneModulesFromCard,
    mergeCompositionModuleRowKeepingScenePlacements,
    normalizeModuleCompositionId,
    resolveMediaModulePanelAnchorRowId,
    resolveModuleCompositionEditableWidthRowId,
    resolveModuleCompositionHoverIdentityKeys,
    revertModuleCompositionRowWidth,
} from "../../../utils/compositionMediaUtils";
import {
    getCompositionTextureSignature,
    pickCompositionTextureIdsFromComposition,
    collectUniqueCompositionTextureIds,
    resolveCompositionTexturesForCustomer3dScene,
} from "../../../utils/compositionTextureAssignment";
import {fetchCompositionTexturePicturesByIds} from "../../../utils/fetchCompositionTexturePicturesByIds";
import Composition3DModuleSizeCell from "./composition3d/Composition3DModuleSizeCell";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {Canvas, useThree} from "@react-three/fiber";
import {Edges, OrbitControls, OrthographicCamera, PerspectiveCamera} from "@react-three/drei";
import Module3DViewBar from "./module3d/Module3DViewBar";
import Module3DToolbar, {resolveCoordinateDraft} from "./module3d/Module3DToolbar";
import ModuleCubeAxes from "./module3d/ModuleCubeAxes";
import PlacedCompositionModuleGroup from "./composition3d/PlacedCompositionModuleGroup";
import {
    buildPlacedCompositionModuleIfPlaced,
    buildPlacedCompositionModuleShellFromPose,
    buildTextureUrlByIdMap,
    compositionModulePlacementDetailData,
    flattenModuleDetailPatterns,
    resolveCompositionDetailTextureUrl,
    getCompositionCubeSizeM,
    getCompositionModuleApiId,
    getCompositionModuleKey,
    getCompositionSceneSignature,
    isCompositionModulePlacedDto,
    isCompositionModuleSizeBlocked,
    normalizeCompositionModuleRow,
} from "./composition3d/compositionModuleScene";
import {
    getModuleCompositionRowPatchId,
    getModulePriorityCoordinates3dAxisLimitsMm,
    resolveModulePriorityCoordinates3dDisplayMm,
} from "./composition3d/compositionModulePriorityMarker3dUtils";
import {
    createCompositionHistorySnapshot,
    loadComposition3DHistory,
    pushHistorySnapshot,
    redoHistory,
    saveComposition3DHistory,
    undoHistory,
} from "./composition3d/composition3dHistory";
import {
    CAMERA_VIEW,
    applyOrthoCameraView,
    applyPerspectiveCameraView,
    getCameraViewPose,
    isOrthographicView,
    isPlanarOrthoView,
} from "./module3d/cameraViews";
import {IDENTITY_AXES, cloneAxes, mirrorAxesWithFaceFlip, normalizeAxesWithFaceFlip, rotateAxesBy90, snapEulerTo90, axesToEulerDegrees} from "./module3d/orientationAxes";
import {
    resolvePlacementPose,
} from "./module3d/snapPlacement";
import {
    createEmbeddedModulePlacement,
    deleteEmbeddedModulePlacement,
    getPacedDtoHide,
    getPacedModuleId,
    invalidateEmbeddedModulePlacementCache,
    parsePacedModuleDto,
    resolveEmbeddedPacedDeleteId,
    resolveEmbeddedPacedExistingDto,
    updateEmbeddedModuleScenePose,
    updatePacedModuleHidden,
} from "./module3d/pacedModule";
import {EditLocalStoragePagesData} from "../../../utils/EditLocalStoragePagesData";
import {withCompositionIdQuery} from "../../../utils/withCompositionIdQuery";
import {preloadCompositionPanelTextures} from "./module3d/detailTextures";

const MM_TO_M = 0.001;

/** Каталог/заказ: стартовая камера чуть ближе, чем в редакторе организации. */
/** Чуть ближе дефолта редактора (2.5); меньше = ближе к сцене. */
const CUSTOMER_CATALOG_3D_CAMERA_DISTANCE_SCALE = 1.85;

/** ПКМ-клик vs панорама: меньше этого сдвига — контекстное меню, больше — орбита. */
const POINTER_CLICK_SLOP_PX = 5;

/** Левая колонка: таблица модулей и панель инструментов (остальное — сцена). */
const COMPOSITION3D_SIDEBAR_WIDTH_PX = 620;
const COMPOSITION3D_SIZE_COLUMN_WIDTH_PX = 216;

const COMPOSITION3D_TABLE_SX = {
    width: "100%",
    minWidth: "100%",
    tableLayout: "fixed",
    "& th": {
        border: "1px solid #EAEBEA",
        backgroundColor: "#F5F6FA",
        padding: "10px",
        paddingBottom: "0.2rem",
        paddingTop: "0.2rem",
        color: "black",
        fontWeight: 700,
    },
    "& td": {
        padding: "0px 0px 0px 10px",
        m: "0px 0px 0px 0px",
        border: "none",
    },
    "& td:not(:last-of-type)": {
        borderRight: "1px solid #EAEBEA",
    },
};

const TABLE_ADD_BUTTON_SX = {
    minWidth: "30px",
    width: "30px",
    height: "30px",
    padding: 0,
    borderRadius: "4px",
    margin: 0,
    flexShrink: 0,
    "&:hover:not(.Mui-disabled)": {
        backgroundColor: (theme) => theme.palette.primary.light,
    },
    "&.Mui-disabled:not(.table-add-muted)": {
        opacity: 1,
    },
    "&.Mui-disabled:not(.table-add-muted).MuiButton-outlinedPrimary": {
        borderColor: (theme) => theme.palette.primary.main,
        color: (theme) => theme.palette.primary.main,
    },
    "&.Mui-disabled:not(.table-add-muted).MuiButton-containedSecondary": {
        backgroundColor: (theme) => theme.palette.secondary.main,
        color: (theme) => theme.palette.secondary.contrastText,
    },
    "&.Mui-disabled.table-add-muted": {
        opacity: 0.38,
    },
    "&.Mui-disabled.table-add-muted.MuiButton-outlinedPrimary": {
        borderColor: (theme) => theme.palette.action.disabled,
        color: (theme) => theme.palette.action.disabled,
    },
};

const TABLE_VISIBILITY_BUTTON_SX = {
    minWidth: 28,
    width: 28,
    height: 28,
    p: 0,
    flexShrink: 0,
    borderRadius: "4px",
    border: (theme) => `1px solid ${theme.palette.primary.main}`,
    color: (theme) => theme.palette.primary.main,
    "&:hover": {
        backgroundColor: (theme) => theme.palette.primary.light50,
    },
    "&.Mui-disabled": {
        opacity: 0.38,
        borderColor: (theme) => theme.palette.action.disabled,
        color: (theme) => theme.palette.action.disabled,
    },
};

const metersToMm3d = (meters) => {
    const n = Number(meters);
    if (!Number.isFinite(n)) return 0;
    return Math.round((n / MM_TO_M) * 1000) / 1000;
};

const TABLE_TEXT_LINK_SX = {
    color: "primary.main",
    textDecoration: "underline",
    cursor: "pointer",
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "bottom",
};

const TABLE_TEXT_LINK_INLINE_SX = {
    display: "inline",
    maxWidth: "100%",
};

const TableTextLink = ({title, to, onClick, children, sx}) => {
    if (!to) {
        return (
            <Box
                component="span"
                title={title}
                sx={{...TABLE_TEXT_LINK_SX, ...sx}}
            >
                {children}
            </Box>
        );
    }

    return (
        <Link
            component={RouterLink}
            to={to}
            title={title}
            underline="hover"
            onClick={(event) => {
                event.stopPropagation();
                onClick?.(event);
            }}
            onMouseDown={(event) => event.stopPropagation()}
            sx={{...TABLE_TEXT_LINK_SX, ...sx}}
        >
            {children}
        </Link>
    );
};

const getModuleCompositionPageId = (row) => {
    const mappedId = row?.moduleCompositionId;
    if (mappedId != null && mappedId !== "" && Number(mappedId) !== 0) {
        return mappedId;
    }
    if (row?.module != null && row?.id != null && row?.id !== "" && Number(row?.id) !== 0) {
        return row.id;
    }
    return null;
};

const buildModuleCompositionPagePath = (row) => {
    const pageId = getModuleCompositionPageId(row);
    return pageId != null ? `/organization/module-composition/${pageId}` : null;
};

/**
 * Ориентир габаритов композиции — только визуальный, клики не перехватывает
 * (иначе ближняя грань мешает выделять модули внутри).
 */
const ReferenceCube = ({sizeM}) => {
    const {width, height, depth} = sizeM;
    const meshRef = useRef();

    useLayoutEffect(() => {
        const root = meshRef.current;
        if (!root) return;
        root.raycast = () => null;
        root.traverse((obj) => {
            obj.raycast = () => null;
        });
    }, []);

    return (
                    <mesh
            ref={meshRef}
            position={[0, height / 2, 0]}
            userData={{module3dRole: "helper"}}
        >
            <boxGeometry args={[width, height, depth]}/>
            <meshStandardMaterial
                color="#2e7d32"
                            transparent
                opacity={0.28}
                depthWrite={false}
                        />
            <Edges color="#1b5e20" threshold={15}/>
                    </mesh>
    );
};

const AnchorPlacementFaces = ({sizeM, enabled, onAddAt}) => {
    if (!enabled) return null;

    const {width, height, depth} = sizeM;
    const inset = 0.002;
    const mat = {
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        depthWrite: false,
    };

    const place = (e) => {
        e.stopPropagation();
        onAddAt?.(e.point, e, "cube");
    };

    return (
        <group userData={{module3dRole: "placement"}}>
            <mesh
                position={[0, inset, 0]}
                rotation={[-Math.PI / 2, 0, 0]}
                onPointerDown={place}
                userData={{module3dRole: "placement"}}
            >
                <planeGeometry args={[Math.max(width * 4, 8), Math.max(depth * 4, 8)]}/>
                <meshBasicMaterial {...mat}/>
            </mesh>
            <mesh
                position={[-width / 2 + inset, height / 2, 0]}
                rotation={[0, Math.PI / 2, 0]}
                onPointerDown={place}
                userData={{module3dRole: "placement"}}
            >
                <planeGeometry args={[Math.max(depth * 2, 4), Math.max(height * 2, 4)]}/>
                <meshBasicMaterial {...mat}/>
            </mesh>
            <mesh
                position={[0, height / 2, -depth / 2 + inset]}
                onPointerDown={place}
                userData={{module3dRole: "placement"}}
            >
                <planeGeometry args={[Math.max(width * 2, 4), Math.max(height * 2, 4)]}/>
                <meshBasicMaterial {...mat}/>
            </mesh>
            </group>
    );
};

const PlacementClickCatcher = ({enabled, onAddAt}) => {
    const {camera, gl, scene} = useThree();

    useEffect(() => {
        if (!enabled) return undefined;
        const el = gl.domElement;

        const onDown = (ev) => {
            if (ev.button !== 0) return;

            const rect = el.getBoundingClientRect();
            const ndc = new THREE.Vector2(
                ((ev.clientX - rect.left) / rect.width) * 2 - 1,
                -((ev.clientY - rect.top) / rect.height) * 2 + 1,
            );
            const raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(ndc, camera);
            const hits = raycaster.intersectObjects(scene.children, true);
            const hit = hits.find((entry) => {
                let obj = entry.object;
                while (obj) {
                    const role = obj.userData?.module3dRole;
                    if (role === "placement" || role === "detail") return true;
                    obj = obj.parent;
                }
                return false;
            });

            const point = new THREE.Vector3();
            if (hit?.point) {
                point.copy(hit.point);
            } else {
                const floor = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
                if (!raycaster.ray.intersectPlane(floor, point)) return;
            }

            ev.stopImmediatePropagation();
            onAddAt?.(point, {
                point,
                object: hit?.object,
                face: hit?.face,
            }, hit ? "detail" : "cube");
        };

        el.addEventListener("pointerdown", onDown, true);
        return () => el.removeEventListener("pointerdown", onDown, true);
    }, [camera, enabled, gl, onAddAt, scene]);

    return null;
};

const CubeAxes = ({sizeM}) => <ModuleCubeAxes sizeM={sizeM}/>;

const TableAddButton = ({
    showMinus = false,
    muted = false,
    disabled = false,
    tooltip = "",
    ariaLabel,
    onClick,
}) => (
    <Tooltip title={tooltip} arrow placement="top">
        <span>
            <Button
                size="small"
                variant={showMinus ? "contained" : "outlined"}
                color={showMinus ? "secondary" : "primary"}
                disabled={disabled}
                className={muted ? "table-add-muted" : undefined}
                onClick={(event) => {
                    event.stopPropagation();
                    onClick?.(event);
                }}
                aria-label={ariaLabel}
                sx={TABLE_ADD_BUTTON_SX}
            >
                {showMinus ? "−" : "+"}
            </Button>
        </span>
    </Tooltip>
);

const TableVisibilityButton = ({
    hidden = false,
    disabled = false,
    tooltip,
    onClick,
}) => {
    const defaultTooltip = hidden ? "Показать на сцене" : "Скрыть на сцене";
    const ariaLabel = hidden ? "Показать на сцене" : "Скрыть на сцене";
    return (
        <Tooltip title={tooltip ?? defaultTooltip} arrow placement="top">
            <span>
                <IconButton
                    size="small"
                    disabled={disabled}
                    aria-label={ariaLabel}
                    onClick={(event) => {
                        event.stopPropagation();
                        onClick?.(event);
                    }}
                    sx={TABLE_VISIBILITY_BUTTON_SX}
                >
                    {hidden
                        ? <VisibilityIcon sx={{fontSize: 18}}/>
                        : <VisibilityOffIcon sx={{fontSize: 18}}/>}
                </IconButton>
            </span>
        </Tooltip>
    );
};

const Composition3DScene = ({
    compositionSizeM,
    activeCameraView,
    cameraViewRequest,
    placedModules,
    selectedInstanceId,
    isolatedModuleInstanceId = null,
    addingMode,
    snapEnabled,
    orthoEnabled,
    showShellTransparent,
    showCompositionShell,
    showModuleShells,
    sceneViewOnly = false,
    hoveredModuleRowId = null,
    hoverHighlightRowIds = null,
    postPendingModuleKey,
    gizmoDraggingRef,
    onAddAt,
    onSelectInstance,
    onTransformEnd,
    onSelectedRightPointerDown = null,
    onSceneContextMenu = null,
    cameraDistanceScale = 2.5,
    compactPriorityMarker = false,
    customerListSceneInteraction = false,
    onCatalogModuleListHoverStart = null,
    onCatalogModuleListHoverEnd = null,
    onCatalogModuleListClick = null,
}) => {
    const extent = Math.max(compositionSizeM.width, compositionSizeM.height, compositionSizeM.depth, 1);
    const useOrtho = isOrthographicView(activeCameraView);
    const rotateEnabled = !isPlanarOrthoView(activeCameraView) && !addingMode;
    const cameraPoseOptions = useMemo(
        () => ({distanceScale: cameraDistanceScale}),
        [cameraDistanceScale],
    );
    const initialPose = useMemo(
        () => getCameraViewPose(compositionSizeM, CAMERA_VIEW.PERSPECTIVE, cameraPoseOptions),
        [compositionSizeM, cameraPoseOptions],
    );
    const {size, set} = useThree();
    const controlsRef = useRef(null);
    const orthoCamRef = useRef(null);
    const perspCamRef = useRef(null);
    const appliedViewRef = useRef(CAMERA_VIEW.PERSPECTIVE);
    const lastViewTokenRef = useRef(null);
    const viewReadyRef = useRef(false);
    const prevUseOrthoRef = useRef(useOrtho);
    const [snapApproach, setSnapApproach] = useState(null);

    const visibleModules = useMemo(
        () => placedModules.filter((inst) => (
            !inst.hidden
            && (!isolatedModuleInstanceId || inst.instanceId === isolatedModuleInstanceId)
        )),
        [placedModules, isolatedModuleInstanceId],
    );

    const snapTargetInstances = useMemo(
        () => visibleModules,
        [visibleModules],
    );

    useLayoutEffect(() => {
        const aspect = size.width / Math.max(size.height, 1);
        const token = cameraViewRequest?.token ?? null;
        const cameraSwitched = prevUseOrthoRef.current !== useOrtho;
        prevUseOrthoRef.current = useOrtho;

        const view = cameraViewRequest?.view || activeCameraView || CAMERA_VIEW.PERSPECTIVE;
        const wantOrtho = isOrthographicView(view);
        const cam = wantOrtho ? orthoCamRef.current : perspCamRef.current;
        if (!cam) return;

        set({camera: cam});
        if (controlsRef.current) {
            controlsRef.current.object = cam;
        }

        const isNewViewRequest =
            !viewReadyRef.current
            || cameraSwitched
            || (token != null && token !== lastViewTokenRef.current)
            || appliedViewRef.current !== view;

        if (isNewViewRequest) {
            const pose = getCameraViewPose(compositionSizeM, view, cameraPoseOptions);
            if (cam.isOrthographicCamera) {
                applyOrthoCameraView(cam, controlsRef.current, pose, aspect);
            } else {
                applyPerspectiveCameraView(cam, controlsRef.current, pose);
            }
            appliedViewRef.current = view;
            lastViewTokenRef.current = token;
            viewReadyRef.current = true;
        }
    }, [activeCameraView, cameraViewRequest, cameraPoseOptions, compositionSizeM, set, size.height, size.width, useOrtho]);

    return (
        <>
            <OrthographicCamera
                ref={orthoCamRef}
                makeDefault={useOrtho}
                position={initialPose.position}
                near={0.01}
                far={Math.max(500, extent * 100)}
            />
            <PerspectiveCamera
                ref={perspCamRef}
                makeDefault={!useOrtho}
                position={initialPose.position}
                fov={45}
                near={0.05}
                far={Math.max(200, extent * 80)}
            />
            <OrbitControls
                ref={controlsRef}
                makeDefault
                target={initialPose.target}
                enableDamping
                dampingFactor={0.08}
                minZoom={0.25}
                maxZoom={12}
                minDistance={Math.max(0.2, extent * 0.15)}
                maxDistance={Math.max(40, extent * 40)}
                enableZoom
                enablePan
                enableRotate={rotateEnabled}
                screenSpacePanning
            />

            <ambientLight intensity={0.75}/>
            <directionalLight position={[5, 8, 4]} intensity={0.95}/>
            <directionalLight position={[-4, 2, -3]} intensity={0.35}/>

            {showCompositionShell && !sceneViewOnly ? <ReferenceCube sizeM={compositionSizeM}/> : null}
            {!sceneViewOnly ? (
                <>
                    <AnchorPlacementFaces sizeM={compositionSizeM} enabled={addingMode} onAddAt={onAddAt}/>
                    <PlacementClickCatcher enabled={addingMode} onAddAt={onAddAt}/>
                </>
            ) : null}

            {visibleModules.map((inst) => {
                const highlightIds = hoverHighlightRowIds;
                const instRowKeys = collectModuleCompositionRowIdentityKeys(inst.row);
                const isListHovered = highlightIds?.size > 0
                    && [...instRowKeys].some((key) => highlightIds.has(key));
                return (
                <PlacedCompositionModuleGroup
                    key={inst.instanceId}
                    inst={inst}
                    parentCompositionSizeM={compositionSizeM}
                    selected={!sceneViewOnly && inst.instanceId === selectedInstanceId}
                    selectionActive={!sceneViewOnly && Boolean(selectedInstanceId)}
                    addingMode={addingMode}
                    snapEnabled={snapEnabled && !sceneViewOnly}
                    otherInstances={snapTargetInstances.filter((o) => (
                        o.instanceId !== inst.instanceId
                    ))}
                    snapApproach={snapApproach}
                    onSnapApproachChange={setSnapApproach}
                    onSelect={sceneViewOnly ? undefined : onSelectInstance}
                    onTransformEnd={sceneViewOnly ? undefined : onTransformEnd}
                    gizmoDraggingRef={gizmoDraggingRef}
                    orthoEnabled={orthoEnabled}
                    showHoles={false}
                    showGrooves={false}
                    showOtherMachining={false}
                    shellTransparent={showShellTransparent}
                    showGreenShell={
                        (showModuleShells && !sceneViewOnly)
                        // Превью каталога: без detailPatterns дети пустые — иначе шкаф невидим.
                        || (customerListSceneInteraction && !(inst.children?.length))
                    }
                    listHoverHighlight={isListHovered}
                    catalogListInteraction={customerListSceneInteraction}
                    onCatalogModuleListHoverStart={onCatalogModuleListHoverStart}
                    onCatalogModuleListHoverEnd={onCatalogModuleListHoverEnd}
                    onCatalogModuleListClick={onCatalogModuleListClick}
                    interactionLocked={sceneViewOnly || postPendingModuleKey === inst.moduleKey}
                    onSelectedRightPointerDown={sceneViewOnly ? null : onSelectedRightPointerDown}
                    onSceneContextMenu={sceneViewOnly ? null : onSceneContextMenu}
                    compositionCameraView={activeCameraView}
                    compactPriorityMarker={compactPriorityMarker}
                />
                );
            })}

            {!sceneViewOnly ? <CubeAxes sizeM={compositionSizeM}/> : null}
        </>
    );
};

const Composition3DView = ({
    compositionId,
    compositionName,
    moduleCompositions = [],
    setModuleCompositions = null,
    compositionLengthLeft,
    compositionLengthRight,
    compositionHeight,
    compositionCornerType,
    onPacedModuleChange = null,
    onReloadModuleCompositions = null,
    handleModalAlert,
    compositionTextures = null,
    useCustomerModuleWidthLockRules = false,
    customerTemplateCompositionId = null,
    customerDraftCompositionId = null,
    onCustomerModuleWidthPatchResponse = null,
    sceneViewOnly = false,
    embeddedLayout = false,
    canvasPreviewOnly = false,
    previewContainerSx = null,
    customerComposition3dPathId = null,
    customerComposition3dDraftId = null,
    customerComposition3dCatalogRequest = false,
    hoveredModuleRowId = null,
    setModuleRowHover = null,
    clearModuleRowHover = null,
    moduleRowHoverPinned = false,
    pinModuleRowHover = null,
    unpinModuleRowHover = null,
    moduleListRowIdPrefix = null,
    customer3dWidthPostResponseRef = null,
    customer3dWidthPostSeq = 0,
}) => {
    const sceneViewOnlyEffective = sceneViewOnly || canvasPreviewOnly;
    const [customer3dComposition, setCustomer3dComposition] = useState(null);
    const [customer3dLoading, setCustomer3dLoading] = useState(false);
    const handleModalAlertRef = useRef(handleModalAlert);
    handleModalAlertRef.current = handleModalAlert;
    const customer3dRequestRef = useRef({});
    customer3dRequestRef.current = {
        pathId: customerComposition3dPathId,
        draftId: customerComposition3dDraftId,
        catalogItemPage: customerComposition3dCatalogRequest,
    };
    const customer3dEnabled = canvasPreviewOnly && customerComposition3dPathId != null
        && customerComposition3dPathId !== "";

    const customer3dCompositionRef = useRef(null);
    customer3dCompositionRef.current = customer3dComposition;

    /**
     * GET /3d — при входе в 3D / смене originalId.
     * draftId читаем из ref на момент запроса; после POST ширины сцена
     * обновляется из ответа (customer3dWidthPostSeq), без повторного GET.
     */
    useEffect(() => {
        if (!customer3dEnabled) {
            setCustomer3dComposition(null);
            setCustomer3dLoading(false);
            return undefined;
        }
        const {pathId, draftId, catalogItemPage} = customer3dRequestRef.current;
        if (pathId == null || pathId === "") {
            return undefined;
        }
        let cancelled = false;
        const isRefresh = customer3dCompositionRef.current != null;
        if (!isRefresh) {
            setCustomer3dLoading(true);
        }
        CustomerCompositionController.get3dSceneComposition(pathId, {
            catalogItemPage,
            draftCompositionId: draftId,
        })
            .then((response) => {
                if (!cancelled) {
                    setCustomer3dComposition(response.data ?? null);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    if (!isRefresh) {
                        setCustomer3dComposition(null);
                    }
                    handleModalAlertRef.current?.(error);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setCustomer3dLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [
        customer3dEnabled,
        customerComposition3dPathId,
    ]);

    useEffect(() => {
        if (!customer3dEnabled || customer3dWidthPostSeq <= 0) {
            return undefined;
        }
        const responseData = customer3dWidthPostResponseRef?.current;
        if (responseData == null) {
            return undefined;
        }
        setCustomer3dComposition((prev) => {
            if (prev == null) {
                return prev;
            }
            return applyCustomer3dSceneCompositionPatch(prev, responseData);
        });
        return undefined;
    }, [customer3dEnabled, customer3dWidthPostSeq, customer3dWidthPostResponseRef]);

    const sceneModuleCompositions = useMemo(() => {
        if (!customer3dEnabled) {
            return moduleCompositions;
        }
        if (!customer3dComposition) {
            return [];
        }
        const from3d = getModuleRowsFromCompositionPayload(customer3dComposition);
        const fromCard = Array.isArray(moduleCompositions) ? moduleCompositions : [];
        // /3d иногда неполный — добираем шкафы с карточки, не ломая detailPatterns/текстуры с /3d.
        return restoreCustomer3dSceneModulesFromCard(fromCard, from3d);
    }, [customer3dEnabled, customer3dComposition, moduleCompositions]);

    const sceneLengthLeft = customer3dEnabled
        ? (compositionLengthLeft ?? customer3dComposition?.lengthLeft)
        : compositionLengthLeft;
    const sceneLengthRight = customer3dEnabled
        ? (compositionLengthRight ?? customer3dComposition?.lengthRight)
        : compositionLengthRight;
    const sceneHeight = customer3dEnabled
        ? (compositionHeight ?? customer3dComposition?.height)
        : compositionHeight;
    const sceneCornerType = customer3dEnabled
        ? (compositionCornerType ?? customer3dComposition?.cornerType)
        : compositionCornerType;

    const [placedModules, setPlacedModules] = useState([]);
    const [selectedInstanceId, setSelectedInstanceId] = useState(null);
    const [addingCandidate, setAddingCandidate] = useState(null);
    const [isPlacing, setIsPlacing] = useState(false);
    const [postPendingModuleKey, setPostPendingModuleKey] = useState(null);
    const [sceneHydrating, setSceneHydrating] = useState(false);
    const [pacedModuleIdByKey, setPacedModuleIdByKey] = useState({});
    const [clearedPacedKeys, setClearedPacedKeys] = useState(new Set());
    const [activeCameraView, setActiveCameraView] = useState(CAMERA_VIEW.PERSPECTIVE);
    const [cameraViewRequest, setCameraViewRequest] = useState(null);
    const [snapEnabled, setSnapEnabled] = useState(true);
    const [orthoEnabled, setOrthoEnabled] = useState(false);
    const [showShellTransparent, setShowShellTransparent] = useState(false);
    const [showCompositionShell, setShowCompositionShell] = useState(true);
    const [showModuleShells, setShowModuleShells] = useState(true);
    const [moduleRowMenu, setModuleRowMenu] = useState(null);
    const [sceneContextMenu, setSceneContextMenu] = useState(null);
    const [isolatedModuleInstanceId, setIsolatedModuleInstanceId] = useState(null);
    const [sceneHistory, setSceneHistory] = useState({past: [], future: []});
    const [textureUrlById, setTextureUrlById] = useState({});
    const [widthEditDialog, setWidthEditDialog] = useState(null);
    const [widthConfirm, setWidthConfirm] = useState(null);
    const [widthSaving, setWidthSaving] = useState(false);
    const [priorityCoordDraft, setPriorityCoordDraft] = useState({x: "", y: ""});
    const [priorityCoordSaving, setPriorityCoordSaving] = useState(false);
    const priorityCoordBaselineRef = useRef({x: null, y: null});
    const priorityCoordEditedAxesRef = useRef(new Set());
    const priorityCoordSkipBlurCommitRef = useRef(false);
    const moduleCompositionsRef = useRef(moduleCompositions);
    const gizmoDraggingRef = useRef(false);
    const historyApplyingRef = useRef(false);
    const scenePointerRef = useRef({x: 0, y: 0, dragged: false});
    const rmbOnSelectedRef = useRef(null);
    const sceneHostPointerCleanupRef = useRef(null);

    const placedModulesRef = useRef([]);
    const pacedModuleIdByKeyRef = useRef({});
    const clearedPacedKeysRef = useRef(new Set());
    const hydrateGenRef = useRef(0);
    const addingCandidateRef = useRef(null);
    const isPlacingRef = useRef(false);
    const selectedInstanceIdRef = useRef(null);

    const compositionSizeM = useMemo(
        () => getCompositionCubeSizeM({
            lengthLeft: sceneLengthLeft,
            lengthRight: sceneLengthRight,
            height: sceneHeight,
            cornerType: sceneCornerType,
        }),
        [
            sceneLengthLeft,
            sceneLengthRight,
            sceneHeight,
            sceneCornerType,
        ],
    );

    const hoverHighlightRowIds = useMemo(
        () => resolveModuleCompositionHoverIdentityKeys(
            hoveredModuleRowId,
            sceneModuleCompositions,
        ),
        [hoveredModuleRowId, sceneModuleCompositions],
    );

    const customerListSceneInteraction = Boolean(
        canvasPreviewOnly
        && setModuleRowHover
        && clearModuleRowHover,
    );

    const handleCatalogModuleListHoverStart = useCallback((row) => {
        if (moduleRowHoverPinned || row?.id == null) {
            return;
        }
        // Справа в списке — нижний шкаф (для TOP якорь = родитель BOTTOM).
        const panelRowId = resolveMediaModulePanelAnchorRowId(row, moduleCompositions)
            ?? normalizeModuleCompositionId(row.id);
        if (panelRowId == null) {
            return;
        }
        setModuleRowHover(panelRowId);
        if (moduleListRowIdPrefix != null) {
            document
                .getElementById(`${moduleListRowIdPrefix}${panelRowId}`)
                ?.scrollIntoView({block: "nearest", behavior: "smooth"});
        }
    }, [
        moduleCompositions,
        moduleListRowIdPrefix,
        moduleRowHoverPinned,
        setModuleRowHover,
    ]);

    const handleCatalogModuleListHoverEnd = useCallback((row) => {
        if (moduleRowHoverPinned || row?.id == null) {
            return;
        }
        const panelRowId = resolveMediaModulePanelAnchorRowId(row, moduleCompositions)
            ?? normalizeModuleCompositionId(row.id);
        if (panelRowId == null) {
            return;
        }
        clearModuleRowHover(panelRowId);
    }, [clearModuleRowHover, moduleCompositions, moduleRowHoverPinned]);

    const handleCatalogModuleListClick = useCallback((row) => {
        if (moduleRowHoverPinned) {
            const panelRowId = resolveMediaModulePanelAnchorRowId(row, moduleCompositions)
                ?? normalizeModuleCompositionId(row?.id);
            unpinModuleRowHover?.(panelRowId ?? null);
            return;
        }
        const editableRowId = resolveModuleCompositionEditableWidthRowId(
            row,
            moduleCompositions,
            {useCustomerWidthLockRules: useCustomerModuleWidthLockRules},
        );
        if (editableRowId == null || moduleListRowIdPrefix == null) {
            return;
        }
        if (pinModuleRowHover) {
            pinModuleRowHover(editableRowId);
        } else {
            setModuleRowHover?.(editableRowId);
        }
        document
            .getElementById(`${moduleListRowIdPrefix}${editableRowId}`)
            ?.scrollIntoView({block: "nearest", behavior: "smooth"});
        focusModuleWidthInput(moduleListRowIdPrefix, editableRowId);
    }, [
        moduleCompositions,
        moduleListRowIdPrefix,
        moduleRowHoverPinned,
        pinModuleRowHover,
        setModuleRowHover,
        unpinModuleRowHover,
        useCustomerModuleWidthLockRules,
    ]);

    const handleCatalogScenePointerMissed = useCallback((event) => {
        if (!canvasPreviewOnly) {
            return;
        }
        if (!moduleRowHoverPinned) {
            return;
        }
        if (scenePointerRef.current.dragged) {
            return;
        }
        if (event?.button != null && event.button !== 0) {
            return;
        }
        unpinModuleRowHover?.(null);
    }, [canvasPreviewOnly, moduleRowHoverPinned, unpinModuleRowHover]);

    const compositionTexturesResolved = useMemo(() => {
        if (customer3dEnabled) {
            return resolveCompositionTexturesForCustomer3dScene(
                customer3dComposition,
                compositionTextures,
            );
        }
        if (compositionTextures != null) {
            return compositionTextures;
        }
        return pickCompositionTextureIdsFromComposition(null);
    }, [
        compositionTextures,
        customer3dEnabled,
        customer3dComposition,
    ]);
    const compositionTextureSignature = useMemo(
        () => getCompositionTextureSignature(compositionTexturesResolved),
        [compositionTexturesResolved],
    );

    const moduleRows = useMemo(
        () => (Array.isArray(sceneModuleCompositions)
            ? sceneModuleCompositions.map(normalizeCompositionModuleRow)
            : []),
        [sceneModuleCompositions],
    );

    const modulePlacementSignature = useMemo(
        () => getCompositionSceneSignature(moduleRows),
        [moduleRows],
    );

    const placedByKey = useMemo(
        () => new Set(placedModules.map((inst) => String(inst.moduleKey))),
        [placedModules],
    );

    const addingMode = Boolean(addingCandidate);
    addingCandidateRef.current = addingCandidate;
    isPlacingRef.current = isPlacing;
    placedModulesRef.current = placedModules;
    pacedModuleIdByKeyRef.current = pacedModuleIdByKey;
    clearedPacedKeysRef.current = clearedPacedKeys;
    selectedInstanceIdRef.current = selectedInstanceId;

    useEffect(() => {
        moduleCompositionsRef.current = moduleCompositions;
    }, [moduleCompositions]);

    const widthConfirmContext = useMemo(() => {
        if (!widthConfirm?.row) {
            return null;
        }
        const formatWidth = (value) => (
            value == null || value === "" ? "—" : `${value} мм`
        );
        return {
            moduleName: getModuleCompositionDisplayName(widthConfirm.row),
            compositionName: compositionName ?? "",
            previousValue: formatWidth(widthConfirm.previous),
            nextValue: formatWidth(widthConfirm.next),
        };
    }, [widthConfirm, compositionName]);

    const buildRowForWidthPatch = useCallback((row, width) => {
        const normalized = normalizeCompositionModuleRow(row);
        const numericWidth = Number(width);
        return {
            ...normalized,
            width: numericWidth,
            module: normalized.module && typeof normalized.module === "object"
                ? {...normalized.module, width: numericWidth}
                : normalized.module,
        };
    }, []);

    const openModuleWidthEditDialog = useCallback((row) => {
        if (widthSaving || widthEditDialog || widthConfirm) {
            return;
        }
        const normalized = normalizeCompositionModuleRow(row);
        if (isCompositionModuleSizeBlocked(normalized, {
            customerRules: useCustomerModuleWidthLockRules,
        })) {
            return;
        }
        const currentWidth = normalized?.module?.width ?? normalized?.width ?? "";
        setWidthEditDialog({
            row: normalized,
            draftWidth: currentWidth === "" || currentWidth == null ? "" : String(currentWidth),
            previousWidth: currentWidth,
        });
    }, [useCustomerModuleWidthLockRules, widthConfirm, widthEditDialog, widthSaving]);

    const closeModuleWidthEditDialog = useCallback(() => {
        setWidthEditDialog(null);
    }, []);

    const submitModuleWidthEditDialog = useCallback(() => {
        if (!widthEditDialog?.row) {
            closeModuleWidthEditDialog();
            return;
        }
        const raw = widthEditDialog.draftWidth;
        if (raw === "" || raw == null) {
            handleModalAlert?.("Укажите ширину");
            return;
        }
        const next = Number(raw);
        if (Number.isNaN(next)) {
            handleModalAlert?.("Некорректная ширина");
            return;
        }
        const previous = widthEditDialog.previousWidth;
        const previousNum = previous === "" || previous == null ? null : Number(previous);
        if (previousNum != null && !Number.isNaN(previousNum) && previousNum === next) {
            closeModuleWidthEditDialog();
            return;
        }
        setWidthConfirm({
            row: widthEditDialog.row,
            previous: previousNum ?? previous,
            next,
        });
        closeModuleWidthEditDialog();
    }, [closeModuleWidthEditDialog, handleModalAlert, widthEditDialog]);

    const cancelModuleWidthConfirm = useCallback(() => {
        setWidthConfirm(null);
    }, []);

    const submitModuleWidthConfirm = useCallback(() => {
        if (!widthConfirm?.row?.id || widthSaving) {
            cancelModuleWidthConfirm();
            return;
        }
        const {row, next, previous} = widthConfirm;
        const moduleCompositionId = row.id;
        const dataForSave = buildRowForWidthPatch(row, next);

        setWidthSaving(true);
        void (async () => {
            try {
                const widthPayload = buildModuleCompositionWidthPatchPayload(
                    dataForSave,
                    moduleCompositionId,
                );
                if (
                    useCustomerModuleWidthLockRules
                    && customerTemplateCompositionId != null
                    && customerTemplateCompositionId !== ""
                ) {
                    const patchRequest = buildCatalogModuleWidthPatchRequest({
                        compositionModule: dataForSave,
                        width: next,
                        templateCompositionId: customerTemplateCompositionId,
                        draftCompositionId: customerDraftCompositionId,
                    });
                    if (patchRequest == null) {
                        throw new Error("Не удалось собрать PATCH module-composition width");
                    }
                    const response = await CustomerCompositionController.module(patchRequest);
                    if (customer3dEnabled) {
                        setCustomer3dComposition((prev) => (
                            prev == null
                                ? prev
                                : applyCustomer3dSceneCompositionPatch(prev, response.data)
                        ));
                    }
                    if (typeof onCustomerModuleWidthPatchResponse === "function") {
                        await onCustomerModuleWidthPatchResponse(response.data);
                    } else if (typeof setModuleCompositions === "function") {
                        setModuleCompositions((prev) => prev.map((item) => {
                            if (item.id !== moduleCompositionId) {
                                return item;
                            }
                            const merged = {...item, width: next};
                            if (item.module && typeof item.module === "object") {
                                merged.module = {...item.module, width: next};
                            }
                            return merged;
                        }));
                    }
                } else {
                    if (widthPayload == null) {
                        throw new Error("Не удалось собрать PATCH module-composition width");
                    }
                    await CompositionController.patchModuleCompositionWidth(
                        moduleCompositionId,
                        widthPayload,
                    );
                    if (typeof setModuleCompositions === "function") {
                        setModuleCompositions((prev) => prev.map((item) => {
                            if (item.id !== moduleCompositionId) {
                                return item;
                            }
                            const merged = {...item, width: next};
                            if (item.module && typeof item.module === "object") {
                                merged.module = {...item.module, width: next};
                            }
                            return merged;
                        }));
                    }
                }
                if (typeof onReloadModuleCompositions === "function") {
                    await onReloadModuleCompositions();
                }
                setWidthConfirm(null);
                handleModalAlert?.("Данные изменены", "success");
            } catch (error) {
                if (
                    typeof setModuleCompositions === "function"
                    && moduleCompositionId != null
                ) {
                    setModuleCompositions((prev) =>
                        prev.map((item) =>
                            item.id === moduleCompositionId
                                ? revertModuleCompositionRowWidth(item, previous)
                                : item,
                        ),
                    );
                }
                setWidthConfirm(null);
                handleModalAlert?.(error);
            } finally {
                setWidthSaving(false);
            }
        })();
    }, [
        buildRowForWidthPatch,
        cancelModuleWidthConfirm,
        customer3dEnabled,
        customerDraftCompositionId,
        customerTemplateCompositionId,
        handleModalAlert,
        onCustomerModuleWidthPatchResponse,
        onReloadModuleCompositions,
        setModuleCompositions,
        useCustomerModuleWidthLockRules,
        widthConfirm,
        widthSaving,
    ]);

    const storageReady = Boolean(compositionId) && !sceneHydrating;

    const takeSceneSnapshot = useCallback(() => createCompositionHistorySnapshot({
        placedModules: placedModulesRef.current,
        selectedInstanceId: selectedInstanceIdRef.current,
        pacedModuleIdByKey: pacedModuleIdByKeyRef.current,
        clearedPacedKeys: clearedPacedKeysRef.current,
    }), []);

    const recordSceneHistory = useCallback(() => {
        if (historyApplyingRef.current || !storageReady) return;
        try {
            const snapshot = takeSceneSnapshot();
            setSceneHistory((prev) => {
                const next = pushHistorySnapshot(prev, snapshot);
                try {
                    saveComposition3DHistory(compositionId, next);
                } catch {
                    // ignore storage errors
                }
                return next;
            });
        } catch (err) {
            console.warn("composition-3d: history snapshot failed", err);
        }
    }, [takeSceneSnapshot, storageReady, compositionId]);

    const syncPacedModuleChange = useCallback((moduleKey, pacedModule) => {
        onPacedModuleChange?.(moduleKey, pacedModule ?? null);
    }, [onPacedModuleChange]);

    const applyModuleHiddenState = useCallback((moduleKey, hidden, pacedDto) => {
        setPlacedModules((prev) => {
            const next = prev.map((inst) => (
                String(inst.moduleKey) === String(moduleKey)
                ? {
                    ...inst,
                        hidden,
                        pacedModule: pacedDto ?? inst.pacedModule,
                        row: {...inst.row, pacedModule: pacedDto ?? inst.row?.pacedModule},
                }
                : inst
            ));
            placedModulesRef.current = next;
            return next;
        });
        syncPacedModuleChange(moduleKey, pacedDto ?? null);
    }, [syncPacedModuleChange]);

    const resolveModulePacedId = useCallback((moduleKey, row = null) => {
        const key = String(moduleKey);
        if (clearedPacedKeysRef.current.has(key)) return null;
        const fromMap = pacedModuleIdByKeyRef.current[moduleKey]
            ?? pacedModuleIdByKeyRef.current[key];
        if (fromMap != null) return fromMap;
        return getPacedModuleId(row);
    }, []);

    /** Восстановить модуль на сцене из pacedModule в GET композиции. */
    const placeModuleOnSceneFromRow = useCallback((row, moduleKey, rowIndex) => {
        const key = String(moduleKey);
        if (moduleKey == null || isPlacingRef.current) return false;
        if (postPendingModuleKey != null) return false;
        if (placedModulesRef.current.some((inst) => String(inst.moduleKey) === key)) return true;
        if (clearedPacedKeysRef.current.has(key)) return false;

        setPostPendingModuleKey(moduleKey);
        try {
            const {inst, rowWithPlacement} = buildPlacedCompositionModuleIfPlaced(
                row,
                rowIndex,
                {compositionSizeM, textureUrlById, compositionTextures: compositionTexturesResolved},
            );
            if (!inst) return false;

            setPlacedModules((prev) => {
                if (prev.some((item) => String(item.moduleKey) === key)) return prev;
                const next = [...prev, inst];
                placedModulesRef.current = next;
                return next;
            });
            const pacedId = inst.pacedModuleId ?? getPacedModuleId(rowWithPlacement ?? row);
            if (pacedId != null) {
                setPacedModuleIdByKey((prev) => ({...prev, [moduleKey]: pacedId}));
            }
            if (
                rowWithPlacement?.pacedModule
                && !isCompositionModulePlacedDto(row)
            ) {
                syncPacedModuleChange(moduleKey, rowWithPlacement.pacedModule);
            }
            return true;
        } catch (err) {
            console.warn("composition-3d: place module from row failed", err);
            return false;
        } finally {
            setPostPendingModuleKey(null);
        }
    }, [compositionSizeM, postPendingModuleKey, syncPacedModuleChange, textureUrlById, compositionTextureSignature]);

    useEffect(() => {
        let cancelled = false;

        if (customer3dEnabled) {
            const from3d = Array.isArray(customer3dComposition?.texturedPictures)
                ? customer3dComposition.texturedPictures
                : [];
            const mapFrom3d = buildTextureUrlByIdMap(from3d);
            const neededIds = collectUniqueCompositionTextureIds(compositionTexturesResolved);
            const missingIds = neededIds.filter((id) => !mapFrom3d[id]);

            if (missingIds.length === 0) {
                setTextureUrlById(mapFrom3d);
                return undefined;
            }

            // Кухни / заказчик: как в остальном customer 3D —
            // GET …/customers/pictures/by-textured/by-ids.
            fetchCompositionTexturePicturesByIds("customer", missingIds)
                .then((extra) => {
                    if (cancelled) {
                        return;
                    }
                    setTextureUrlById({
                        ...mapFrom3d,
                        ...buildTextureUrlByIdMap(extra),
                    });
                })
                .catch(() => {
                    if (!cancelled) {
                        setTextureUrlById(mapFrom3d);
                    }
                });
            return () => {
                cancelled = true;
            };
        }

        const orgTextureIds = collectUniqueCompositionTextureIds(compositionTexturesResolved);
        if (orgTextureIds.length === 0) {
            setTextureUrlById({});
            return undefined;
        }

        fetchCompositionTexturePicturesByIds("organization", orgTextureIds)
            .then((list) => {
                if (!cancelled) {
                    setTextureUrlById(buildTextureUrlByIdMap(list));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setTextureUrlById({});
                }
            });
        return () => {
            cancelled = true;
        };
    }, [
        compositionId,
        customer3dEnabled,
        customer3dComposition?.texturedPictures,
        compositionTextureSignature,
    ]);

    useEffect(() => {
        const urls = [];
        for (const row of moduleRows) {
            for (const detail of flattenModuleDetailPatterns(row)) {
                const url = resolveCompositionDetailTextureUrl(
                    detail,
                    textureUrlById,
                    compositionTexturesResolved,
                );
                if (url) {
                    urls.push(url);
                }
            }
        }
        preloadCompositionPanelTextures(urls);
    }, [moduleRows, textureUrlById, compositionTextureSignature]);

    useEffect(() => {
        setClearedPacedKeys(new Set());
        clearedPacedKeysRef.current = new Set();
        setSelectedInstanceId(null);
        setAddingCandidate(null);
        addingCandidateRef.current = null;
        setPostPendingModuleKey(null);
        setIsPlacing(false);
        isPlacingRef.current = false;
        setSceneHistory({past: [], future: []});
    }, [compositionId]);

    useEffect(() => {
        if (compositionId == null || compositionId === "") {
            setPlacedModules([]);
            setPacedModuleIdByKey({});
            setSceneHydrating(false);
            return undefined;
        }

        const gen = ++hydrateGenRef.current;
        let cancelled = false;
        const tasks = moduleRows.map((row, index) => ({row, index}));

        if (!tasks.length) {
            setPlacedModules([]);
            placedModulesRef.current = [];
            setPacedModuleIdByKey({});
            setSceneHydrating(false);
            if (!canvasPreviewOnly) {
                setSceneHistory(loadComposition3DHistory(compositionId));
            }
            return undefined;
        }

        setSceneHydrating(true);

        const results = tasks.map(({row, index}) => {
            const moduleKey = getCompositionModuleKey(row, index);
            const key = String(moduleKey);
            if (clearedPacedKeysRef.current.has(key)) {
                return {
                    moduleKey,
                    inst: null,
                    rowWithPlacement: {...row, pacedModule: null},
                    sourceRow: row,
                };
            }
            const prevInst = placedModulesRef.current.find(
                (item) => String(item.moduleKey) === key,
            );
            const rowForBuild = prevInst?.row != null
                ? mergeCompositionModuleRowKeepingScenePlacements(prevInst.row, row)
                : row;
            try {
                let {inst, rowWithPlacement} = buildPlacedCompositionModuleIfPlaced(
                    rowForBuild,
                    index,
                    {compositionSizeM, textureUrlById, compositionTextures: compositionTexturesResolved},
                );
                if (
                    !inst
                    && prevInst?.localPosition
                ) {
                    inst = buildPlacedCompositionModuleShellFromPose({
                        row: rowForBuild,
                        index,
                        localPosition: prevInst.localPosition,
                        axes: prevInst.axes || IDENTITY_AXES,
                        faceFlip: Boolean(prevInst.faceFlip),
                        pacedModule: prevInst.pacedModule,
                        instanceId: prevInst.instanceId,
                        textureUrlById,
                        compositionTextures: compositionTexturesResolved,
                    });
                    rowWithPlacement = rowForBuild;
                } else if (
                    inst
                    && prevInst?.localPosition
                    && (!(inst.children?.length) && prevInst.children?.length)
                ) {
                    inst = buildPlacedCompositionModuleShellFromPose({
                        row: rowForBuild,
                        index,
                        localPosition: inst.localPosition ?? prevInst.localPosition,
                        axes: inst.axes || prevInst.axes || IDENTITY_AXES,
                        faceFlip: Boolean(inst.faceFlip ?? prevInst.faceFlip),
                        pacedModule: inst.pacedModule ?? prevInst.pacedModule,
                        instanceId: prevInst.instanceId ?? inst.instanceId,
                        textureUrlById,
                        compositionTextures: compositionTexturesResolved,
                    }) ?? inst;
                }
                return {moduleKey, inst, rowWithPlacement, sourceRow: row};
            } catch (err) {
                console.warn("composition-3d: hydrate module failed", err);
                return {moduleKey, inst: null, rowWithPlacement: row, sourceRow: row};
            }
        });

        if (!cancelled && gen === hydrateGenRef.current) {
            const nextPlaced = [];
            const nextIds = {};
            for (const {moduleKey, inst} of results) {
                const key = String(moduleKey);
                if (clearedPacedKeysRef.current.has(key)) continue;
                if (inst) {
                    const prevInst = placedModulesRef.current.find(
                        (item) => String(item.moduleKey) === key,
                    );
                    nextPlaced.push({
                        ...inst,
                        instanceId: prevInst?.instanceId ?? inst.instanceId,
                    });
                    if (inst.pacedModuleId != null) {
                        nextIds[moduleKey] = inst.pacedModuleId;
                    }
                }
            }
            setPlacedModules(nextPlaced);
            placedModulesRef.current = nextPlaced;
            setPacedModuleIdByKey((prev) => ({...prev, ...nextIds}));
            setSceneHydrating(false);
            if (!canvasPreviewOnly) {
                setSceneHistory(loadComposition3DHistory(compositionId));
            }
        }

        return () => {
            cancelled = true;
            setSceneHydrating(false);
        };
    }, [
        canvasPreviewOnly,
        compositionId,
        compositionSizeM,
        modulePlacementSignature,
        textureUrlById,
        compositionTextureSignature,
    ]);

    const applyCameraView = useCallback((view) => {
        setActiveCameraView(view);
        setCameraViewRequest({view, token: Date.now()});
    }, []);

    const persistModuleScenePose = useCallback((inst, previousInst = null) => {
        if (!inst) return;
        const currentInst = placedModulesRef.current.find(
            (row) => row.instanceId === inst.instanceId,
        ) ?? inst;
        const pacedId = resolveEmbeddedPacedDeleteId({
            moduleKey: currentInst.moduleKey,
            inst: {
                ...currentInst,
                dependModuleId: currentInst.moduleApiId,
            },
            pacedEmbeddedIdByKey: pacedModuleIdByKeyRef.current,
        });
        if (pacedId == null) return;

        void updateEmbeddedModuleScenePose({
            pacedModuleId: pacedId,
            localPosition: inst.localPosition,
            axes: inst.axes || IDENTITY_AXES,
            faceFlip: Boolean(inst.faceFlip),
            existingDto: resolveEmbeddedPacedExistingDto(currentInst),
            dependModuleId: currentInst.moduleApiId,
        }).then((data) => {
            if (!data) {
                handleModalAlert?.("Нет связи с сервером. Положение не удалось сохранить.");
                if (previousInst) {
                    setPlacedModules((prev) => prev.map((row) => (
                        row.instanceId === previousInst.instanceId ? previousInst : row
                    )));
                }
                return;
            }
            invalidateEmbeddedModulePlacementCache(currentInst.moduleApiId);
            const pacedPose = parsePacedModuleDto(data);
            setPlacedModules((prev) => {
                const next = prev.map((row) => (
                    row.instanceId === inst.instanceId
                        ? {
                            ...row,
                            hidden: getPacedDtoHide(data),
                            pacedModule: data,
                            pacedModuleId: data.id ?? row.pacedModuleId,
                            row: {...row.row, pacedModule: data},
                            ...(pacedPose?.localPosition
                                ? {localPosition: pacedPose.localPosition}
                                : {}),
                            ...(pacedPose?.axes
                                ? {
                                    axes: pacedPose.axes,
                                    faceFlip: Boolean(pacedPose.faceFlip),
                                    euler: axesToEulerDegrees(pacedPose.axes),
                                }
                                : {}),
                        }
                        : row
                ));
                placedModulesRef.current = next;
                return next;
            });
            syncPacedModuleChange(inst.moduleKey, data);
        });
    }, [handleModalAlert, syncPacedModuleChange]);

    const applySceneHistorySnapshot = useCallback((snapshot) => {
        if (!snapshot) return;
        historyApplyingRef.current = true;
        const prevList = placedModulesRef.current;
        const prevById = new Map(prevList.map((inst) => [inst.instanceId, inst]));
        const prevByKey = new Map(prevList.map((inst) => [String(inst.moduleKey), inst]));
        const nextSlim = Array.isArray(snapshot.placedModules) ? snapshot.placedModules : [];

        const nextList = nextSlim.map((slim) => {
            const full = prevById.get(slim.instanceId)
                || (slim.moduleKey != null ? prevByKey.get(String(slim.moduleKey)) : null);
            if (full) {
                const pacedModule = slim.pacedModule ?? full.pacedModule;
                return {
                    ...full,
                    instanceId: slim.instanceId ?? full.instanceId,
                    localPosition: slim.localPosition ?? full.localPosition,
                    axes: slim.axes ?? full.axes,
                    faceFlip: slim.faceFlip ?? full.faceFlip,
                    euler: slim.euler ?? full.euler,
                    hidden: slim.hidden ?? full.hidden,
                    pacedModuleId: slim.pacedModuleId ?? full.pacedModuleId,
                    pacedModule,
                    row: full.row
                        ? {...full.row, pacedModule: pacedModule ?? full.row.pacedModule}
                        : full.row,
                };
            }

            const rowIndex = moduleRows.findIndex(
                (row, index) => String(getCompositionModuleKey(row, index)) === String(slim.moduleKey),
            );
            const row = slim.row ?? (rowIndex >= 0 ? moduleRows[rowIndex] : null);
            if (row && slim.localPosition) {
                const inst = buildPlacedCompositionModuleShellFromPose({
                    row,
                    index: rowIndex >= 0 ? rowIndex : 0,
                    localPosition: slim.localPosition,
                    axes: slim.axes || IDENTITY_AXES,
                    faceFlip: Boolean(slim.faceFlip),
                    pacedModule: slim.pacedModule ?? row.pacedModule,
                    instanceId: slim.instanceId,
                    textureUrlById,
                    compositionTextures: compositionTexturesResolved,
                });
                if (inst) {
                    return {
                        ...inst,
                        hidden: Boolean(slim.hidden),
                        pacedModuleId: slim.pacedModuleId ?? inst.pacedModuleId,
                    };
                }
            }
            return slim;
        });

        setPlacedModules(nextList);
        placedModulesRef.current = nextList;
        setSelectedInstanceId(snapshot.selectedInstanceId ?? null);

        const nextPacedIds = snapshot.pacedModuleIdByKey || {};
        setPacedModuleIdByKey(nextPacedIds);
        pacedModuleIdByKeyRef.current = nextPacedIds;

        const nextCleared = new Set(snapshot.clearedPacedKeys || []);
        setClearedPacedKeys(nextCleared);
        clearedPacedKeysRef.current = nextCleared;

        const snapshotKeys = new Set(nextSlim.map((slim) => String(slim.moduleKey)));
        moduleRows.forEach((row, index) => {
            const moduleKey = getCompositionModuleKey(row, index);
            const key = String(moduleKey);
            if (nextCleared.has(key)) {
                syncPacedModuleChange(moduleKey, null);
                return;
            }
            if (!snapshotKeys.has(key)) return;
            const inst = nextList.find((item) => String(item.moduleKey) === key);
            syncPacedModuleChange(moduleKey, inst?.pacedModule ?? inst?.row?.pacedModule ?? null);
        });

        nextList.forEach((inst) => {
            const prev = prevById.get(inst.instanceId)
                || prevByKey.get(String(inst.moduleKey));
            if (!prev) return;
            const samePose = (
                prev.localPosition?.x === inst.localPosition?.x
                && prev.localPosition?.y === inst.localPosition?.y
                && prev.localPosition?.z === inst.localPosition?.z
                && Boolean(prev.faceFlip) === Boolean(inst.faceFlip)
                && JSON.stringify(prev.axes) === JSON.stringify(inst.axes)
            );
            if (!samePose) {
                persistModuleScenePose(inst, prev);
            }
        });

        window.setTimeout(() => {
            historyApplyingRef.current = false;
        }, 0);
    }, [moduleRows, persistModuleScenePose, syncPacedModuleChange]);

    const undoSceneChange = useCallback(() => {
        if (historyApplyingRef.current || addingMode || isPlacing) return;
        const current = takeSceneSnapshot();
        const {history: nextHistory, restored} = undoHistory(sceneHistory, current);
        if (!restored) return;
        setSceneHistory(nextHistory);
        saveComposition3DHistory(compositionId, nextHistory);
        applySceneHistorySnapshot(restored);
    }, [
        takeSceneSnapshot,
        sceneHistory,
        compositionId,
        applySceneHistorySnapshot,
        addingMode,
        isPlacing,
    ]);

    const redoSceneChange = useCallback(() => {
        if (historyApplyingRef.current || addingMode || isPlacing) return;
        const current = takeSceneSnapshot();
        const {history: nextHistory, restored} = redoHistory(sceneHistory, current);
        if (!restored) return;
        setSceneHistory(nextHistory);
        saveComposition3DHistory(compositionId, nextHistory);
        applySceneHistorySnapshot(restored);
    }, [
        takeSceneSnapshot,
        sceneHistory,
        compositionId,
        applySceneHistorySnapshot,
        addingMode,
        isPlacing,
    ]);

    const commitModulePose = useCallback((instanceId, next) => {
        const prevInst = placedModulesRef.current.find((row) => row.instanceId === instanceId);
        if (!prevInst) return;
        recordSceneHistory();
        const axes = next.axes != null
            ? cloneAxes(next.axes)
            : cloneAxes(prevInst.axes || IDENTITY_AXES);
        const faceFlip = next.faceFlip != null
            ? Boolean(next.faceFlip)
            : Boolean(prevInst.faceFlip);
        const merged = {
            ...prevInst,
            localPosition: next.localPosition ?? prevInst.localPosition,
                    axes,
                    faceFlip,
            euler: snapEulerTo90(next.euler ?? axesToEulerDegrees(axes)),
        };
        setPlacedModules((prev) => {
            const nextList = prev.map((row) => (row.instanceId === instanceId ? merged : row));
            placedModulesRef.current = nextList;
            return nextList;
        });
        persistModuleScenePose(merged, prevInst);
    }, [persistModuleScenePose, recordSceneHistory]);

    const selectedInstance = useMemo(
        () => placedModules.find((inst) => inst.instanceId === selectedInstanceId) ?? null,
        [placedModules, selectedInstanceId],
    );

    const basePointMm = useMemo(() => {
        if (!selectedInstance?.localPosition) return null;
        return {
            x: metersToMm3d(selectedInstance.localPosition.x),
            y: metersToMm3d(selectedInstance.localPosition.y),
            z: metersToMm3d(selectedInstance.localPosition.z),
        };
    }, [selectedInstance]);

    const commitBasePointMm = useCallback((nextMm) => {
        if (!selectedInstanceId) return;
        commitModulePose(selectedInstanceId, {
            localPosition: {
                x: Number.isFinite(Number(nextMm?.x)) ? Number(nextMm.x) * MM_TO_M : 0,
                y: Number.isFinite(Number(nextMm?.y)) ? Number(nextMm.y) * MM_TO_M : 0,
                z: Number.isFinite(Number(nextMm?.z)) ? Number(nextMm.z) * MM_TO_M : 0,
            },
        });
    }, [selectedInstanceId, commitModulePose]);

    useEffect(() => {
        if (!selectedInstance?.row) {
            setPriorityCoordDraft({x: "", y: ""});
            priorityCoordBaselineRef.current = {x: null, y: null};
            priorityCoordEditedAxesRef.current.clear();
            return;
        }
        const {coordinateX3DPoint, coordinateY3DPoint} =
            resolveModulePriorityCoordinates3dDisplayMm(selectedInstance.row);
        const x = coordinateX3DPoint ?? "";
        const y = coordinateY3DPoint ?? "";
        setPriorityCoordDraft({x, y});
        priorityCoordBaselineRef.current = {x, y};
        priorityCoordEditedAxesRef.current.clear();
    }, [
        selectedInstanceId,
        selectedInstance?.row,
        selectedInstance?.row?.id,
        selectedInstance?.row?.moduleCompositionId,
        selectedInstance?.row?.coordinateX3DPoint,
        selectedInstance?.row?.coordinateY3DPoint,
        selectedInstance?.row?.module?.width,
        selectedInstance?.row?.module?.height,
        selectedInstance?.row?.module?.depth,
    ]);

    const priorityCoordInputsDisabled =
        !selectedInstanceId
        || addingMode
        || isPlacing
        || Boolean(postPendingModuleKey)
        || priorityCoordSaving
        || sceneViewOnlyEffective;

    const priorityCoordLimits = useMemo(
        () => getModulePriorityCoordinates3dAxisLimitsMm(selectedInstance?.row),
        [
            selectedInstance?.row,
            selectedInstance?.row?.module?.width,
            selectedInstance?.row?.module?.height,
            selectedInstance?.row?.module?.depth,
        ],
    );

    const resolvePriorityCoordAxisValue = useCallback((axis, rawValue) => {
        const baseline = priorityCoordBaselineRef.current;
        const current = axis === "x"
            ? Number(baseline.x) || 0
            : Number(baseline.y) || 0;
        const text = String(rawValue ?? "").trim();
        const useRelative = priorityCoordEditedAxesRef.current.has(axis)
            || (/^[+-]/.test(text) && text.length > 1);
        let resolved = resolveCoordinateDraft(rawValue, current, useRelative);
        resolved = Math.round(resolved);
        const limits = priorityCoordLimits?.[axis];
        if (limits) {
            resolved = Math.min(limits.max, Math.max(limits.min, resolved));
        }
        return resolved;
    }, [priorityCoordLimits]);

    const applyPriorityCoordDraftResolution = useCallback((draft, axesToResolve = ["x", "y"]) => {
        const next = {...draft};
        axesToResolve.forEach((axis) => {
            next[axis] = String(resolvePriorityCoordAxisValue(axis, draft[axis]));
            priorityCoordEditedAxesRef.current.delete(axis);
        });
        return next;
    }, [resolvePriorityCoordAxisValue]);

    const commitPriorityCoordinate3d = useCallback(async (draftOverride) => {
        const row = selectedInstance?.row;
        const moduleCompositionRowId = getModuleCompositionRowPatchId(row);
        if (moduleCompositionRowId == null || priorityCoordInputsDisabled) {
            return;
        }
        const draftIn = draftOverride ?? priorityCoordDraft;
        const resolvedDraft = applyPriorityCoordDraftResolution({...draftIn}, ["x", "y"]);
        setPriorityCoordDraft(resolvedDraft);
        const nextX = resolvedDraft.x;
        const nextY = resolvedDraft.y;
        const {x: prevX, y: prevY} = priorityCoordBaselineRef.current;
        if (String(nextX ?? "") === String(prevX ?? "") && String(nextY ?? "") === String(prevY ?? "")) {
            return;
        }
        if (nextX === "" || nextX == null || nextY === "" || nextY == null) {
            handleModalAlert?.("Укажите обе координаты приоритета (X и Y)");
            setPriorityCoordDraft({x: prevX ?? "", y: prevY ?? ""});
            return;
        }
        const nx = Number(nextX);
        const ny = Number(nextY);
        if (Number.isNaN(nx) || Number.isNaN(ny)) {
            handleModalAlert?.("Некорректные координаты 3D");
            setPriorityCoordDraft({x: prevX ?? "", y: prevY ?? ""});
            return;
        }
        setPriorityCoordSaving(true);
        try {
            const response = await CompositionController.patchModuleCompositionCoordinate3d(
                moduleCompositionRowId,
                nx,
                ny,
            );
            const patchedX = response.data?.coordinateX3DPoint ?? Math.round(nx);
            const patchedY = response.data?.coordinateY3DPoint ?? Math.round(ny);
            const patchRowFields = {
                coordinateX3DPoint: patchedX,
                coordinateY3DPoint: patchedY,
            };
            const rowMatchesPatch = (item) => (
                getModuleCompositionRowPatchId(item) === moduleCompositionRowId
                    || item.id === row.id
            );
            if (typeof setModuleCompositions === "function") {
                setModuleCompositions((prev) => prev.map((item) => (
                    rowMatchesPatch(item) ? {...item, ...patchRowFields} : item
                )));
            }
            setPlacedModules((prev) => {
                const next = prev.map((inst) => (
                    rowMatchesPatch(inst.row)
                        ? {...inst, row: {...inst.row, ...patchRowFields}}
                        : inst
                ));
                placedModulesRef.current = next;
                return next;
            });
            priorityCoordBaselineRef.current = {
                x: patchedX ?? "",
                y: patchedY ?? "",
            };
            setPriorityCoordDraft({
                x: patchedX ?? "",
                y: patchedY ?? "",
            });
            handleModalAlert?.("Данные изменены", "success");
        } catch (error) {
            setPriorityCoordDraft({x: prevX ?? "", y: prevY ?? ""});
            handleModalAlert?.(error);
        } finally {
            setPriorityCoordSaving(false);
        }
    }, [
        applyPriorityCoordDraftResolution,
        handleModalAlert,
        priorityCoordDraft,
        priorityCoordInputsDisabled,
        selectedInstance?.row,
        setModuleCompositions,
    ]);

    const handlePriorityCoordinateBlur = useCallback((axis) => {
        if (priorityCoordSkipBlurCommitRef.current) {
            return;
        }
        const resolvedDraft = applyPriorityCoordDraftResolution({...priorityCoordDraft}, [axis]);
        setPriorityCoordDraft(resolvedDraft);
        void commitPriorityCoordinate3d(resolvedDraft);
    }, [
        applyPriorityCoordDraftResolution,
        commitPriorityCoordinate3d,
        priorityCoordDraft,
    ]);

    const handlePriorityCoordinateCommit = useCallback((axis, rawValue) => {
        if (priorityCoordInputsDisabled) {
            return;
        }
        priorityCoordEditedAxesRef.current.add(axis);
        setPriorityCoordDraft((prev) => {
            const draft = {...prev, [axis]: String(rawValue)};
            const resolvedDraft = applyPriorityCoordDraftResolution(draft, [axis]);
            void commitPriorityCoordinate3d(resolvedDraft);
            return resolvedDraft;
        });
    }, [
        applyPriorityCoordDraftResolution,
        commitPriorityCoordinate3d,
        priorityCoordInputsDisabled,
    ]);

    const handlePriorityCoordinateKeyDown = useCallback((event, axis) => {
        if (priorityCoordInputsDisabled) {
            return;
        }
        if (event.key !== "Enter") {
            return;
        }
        event.preventDefault();
        priorityCoordSkipBlurCommitRef.current = true;
        setPriorityCoordDraft((prev) => {
            const resolvedDraft = applyPriorityCoordDraftResolution({...prev}, [axis]);
            void commitPriorityCoordinate3d(resolvedDraft);
            return resolvedDraft;
        });
        event.currentTarget.blur();
        queueMicrotask(() => {
            priorityCoordSkipBlurCommitRef.current = false;
        });
    }, [
        applyPriorityCoordDraftResolution,
        commitPriorityCoordinate3d,
        priorityCoordInputsDisabled,
    ]);

    const rotateSelectedBy90 = useCallback((axis, sign) => {
        if (!selectedInstanceId) return;
        const inst = placedModulesRef.current.find((row) => row.instanceId === selectedInstanceId);
        if (!inst) return;
        const axes = rotateAxesBy90(inst.axes || IDENTITY_AXES, axis, sign);
        commitModulePose(selectedInstanceId, {
            axes,
            faceFlip: Boolean(inst.faceFlip),
            euler: snapEulerTo90(axesToEulerDegrees(axes)),
        });
    }, [selectedInstanceId, commitModulePose]);

    const mirrorSelected = useCallback((axis) => {
        if (!selectedInstanceId) return;
        const inst = placedModulesRef.current.find((row) => row.instanceId === selectedInstanceId);
        if (!inst) return;
        const mirrored = mirrorAxesWithFaceFlip(
            inst.axes || IDENTITY_AXES,
            axis,
            inst.faceFlip,
        );
        const {axes: rightAxes} = normalizeAxesWithFaceFlip(mirrored.axes, false);
        commitModulePose(selectedInstanceId, {
            axes: mirrored.axes,
            faceFlip: mirrored.faceFlip,
            euler: snapEulerTo90(axesToEulerDegrees(rightAxes)),
        });
    }, [selectedInstanceId, commitModulePose]);

    const toggleModuleVisibility = useCallback(async (moduleKey, row, nextHidden) => {
        const pacedId = resolveModulePacedId(moduleKey, row);
        if (pacedId == null) return false;
        recordSceneHistory();
        const inst = placedModulesRef.current.find(
            (item) => String(item.moduleKey) === String(moduleKey),
        ) ?? null;
        const existingDto = resolveEmbeddedPacedExistingDto(inst) ?? row?.pacedModule ?? null;
        const dto = await updatePacedModuleHidden({
            pacedModuleId: pacedId,
            hide: nextHidden,
            existingDto,
            dependModuleId: getCompositionModuleApiId(row),
        });
        if (!dto) {
            handleModalAlert?.("Не удалось изменить видимость модуля.");
            return false;
        }
        applyModuleHiddenState(moduleKey, nextHidden, dto);
        if (nextHidden) {
            setSelectedInstanceId((curr) => {
                const hiddenInst = placedModulesRef.current.find(
                    (item) => String(item.moduleKey) === String(moduleKey),
                );
                return hiddenInst?.instanceId === curr ? null : curr;
            });
        }
        return true;
    }, [resolveModulePacedId, applyModuleHiddenState, handleModalAlert, recordSceneHistory]);

    const setAllModulesVisibility = useCallback(async (hidden, exceptModuleKey = null) => {
        const targets = placedModulesRef.current.filter((inst) => {
            if (hidden) {
                if (exceptModuleKey != null) {
                    return String(inst.moduleKey) !== String(exceptModuleKey) && !inst.hidden;
                }
                return !inst.hidden;
            }
            return inst.hidden;
        });
        if (!targets.length) return true;
        recordSceneHistory();

        const results = await Promise.all(targets.map(async (inst) => {
            const pacedId = resolveModulePacedId(inst.moduleKey, inst.row);
            if (pacedId == null) return {ok: false, inst, dto: null};
            const dto = await updatePacedModuleHidden({
                pacedModuleId: pacedId,
                hide: hidden,
                existingDto: resolveEmbeddedPacedExistingDto(inst) ?? inst.row?.pacedModule ?? null,
                dependModuleId: inst.moduleApiId ?? getCompositionModuleApiId(inst.row),
            });
            return {ok: Boolean(dto), inst, dto};
        }));

        const failed = results.filter((result) => !result.ok);
        results.forEach(({ok, inst, dto}) => {
            if (!ok || !dto) return;
            applyModuleHiddenState(inst.moduleKey, hidden, dto);
        });

        if (failed.length) {
            handleModalAlert?.("Не удалось изменить видимость модулей.");
        }

        setIsolatedModuleInstanceId(null);
        setSceneContextMenu(null);
        setModuleRowMenu(null);
        if (hidden) {
            setSelectedInstanceId((curr) => {
                const stillVisible = placedModulesRef.current.some(
                    (inst) => inst.instanceId === curr && !inst.hidden,
                );
                return stillVisible ? curr : null;
            });
        }
        return failed.length === 0;
    }, [resolveModulePacedId, applyModuleHiddenState, handleModalAlert, recordSceneHistory]);

    const revealAllModules = useCallback(() => {
        setIsolatedModuleInstanceId(null);
        setSceneContextMenu(null);
        void setAllModulesVisibility(false);
    }, [setAllModulesVisibility]);

    const hideSceneModule = useCallback((instanceId) => {
        if (!instanceId) return;
        const inst = placedModulesRef.current.find((item) => item.instanceId === instanceId);
        if (!inst) return;
        void toggleModuleVisibility(inst.moduleKey, inst.row, true);
        setIsolatedModuleInstanceId((curr) => (curr === instanceId ? null : curr));
        setSceneContextMenu(null);
    }, [toggleModuleVisibility]);

    const clearSceneIsolation = useCallback(() => {
        setIsolatedModuleInstanceId(null);
        setSceneContextMenu(null);
    }, []);

    const markSelectedRightPointerDown = useCallback((instanceId) => {
        rmbOnSelectedRef.current = instanceId;
    }, []);

    const openSceneContextMenu = useCallback((instanceId, nativeEvent) => {
        if (addingMode || isPlacing || !instanceId) return;
        if (scenePointerRef.current.dragged) return;
        nativeEvent?.preventDefault?.();
            setSelectedInstanceId(instanceId);
        setSceneContextMenu({
            mouseX: nativeEvent?.clientX ?? 0,
            mouseY: nativeEvent?.clientY ?? 0,
            instanceId,
        });
    }, [addingMode, isPlacing]);

    const setSceneHostNode = useCallback((node) => {
        sceneHostPointerCleanupRef.current?.();
        sceneHostPointerCleanupRef.current = null;
        if (!node) return;
        const onDown = (event) => {
            scenePointerRef.current = {x: event.clientX, y: event.clientY, dragged: false};
            rmbOnSelectedRef.current = null;
        };
        const onMove = (event) => {
            const state = scenePointerRef.current;
            if (state.dragged) return;
            if (Math.hypot(event.clientX - state.x, event.clientY - state.y) > POINTER_CLICK_SLOP_PX) {
                state.dragged = true;
            }
        };
        node.addEventListener("pointerdown", onDown, true);
        window.addEventListener("pointermove", onMove);
        sceneHostPointerCleanupRef.current = () => {
            node.removeEventListener("pointerdown", onDown, true);
            window.removeEventListener("pointermove", onMove);
        };
    }, []);

    useEffect(() => () => {
        sceneHostPointerCleanupRef.current?.();
        sceneHostPointerCleanupRef.current = null;
    }, []);

    const openModuleRowMenu = useCallback((event, row, moduleKey) => {
        setModuleRowMenu({anchorEl: event.currentTarget, moduleKey, row});
    }, []);

    const closeModuleRowMenu = useCallback(() => {
        setModuleRowMenu(null);
    }, []);

    const applyDeletedPacedState = useCallback((moduleKey) => {
        setPacedModuleIdByKey((prev) => {
            if (!(moduleKey in prev)) return prev;
            const next = {...prev};
            delete next[moduleKey];
            pacedModuleIdByKeyRef.current = next;
            return next;
        });
        setClearedPacedKeys((prev) => {
            const key = String(moduleKey);
            if (prev.has(key)) return prev;
            const next = new Set(prev);
            next.add(key);
            clearedPacedKeysRef.current = next;
            return next;
        });
        syncPacedModuleChange(moduleKey, null);
    }, [syncPacedModuleChange]);

    const removeModuleByKey = useCallback(async (moduleKey, row = null) => {
        if (moduleKey == null) return;

        const prevPlaced = placedModulesRef.current;
        const prevInst = prevPlaced.find(
            (inst) => String(inst.moduleKey) === String(moduleKey),
        ) ?? null;
        const prevPacedDto = prevInst?.pacedModule ?? row?.pacedModule ?? null;

        recordSceneHistory();
        applyDeletedPacedState(moduleKey);
        setPlacedModules((prev) => prev.filter(
            (inst) => String(inst.moduleKey) !== String(moduleKey),
        ));
        setAddingCandidate((curr) => (
            curr?.moduleKey != null && String(curr.moduleKey) === String(moduleKey) ? null : curr
        ));

        const pacedId = resolveEmbeddedPacedDeleteId({
            moduleKey,
            inst: prevInst ? {...prevInst, dependModuleId: prevInst.moduleApiId} : null,
            pacedEmbeddedIdByKey: pacedModuleIdByKeyRef.current,
        });
        if (pacedId == null) return;

        const ok = await deleteEmbeddedModulePlacement(pacedId, prevInst?.moduleApiId);
        if (ok) return;

        setPlacedModules(prevPlaced);
        setClearedPacedKeys((prev) => {
            const next = new Set(prev);
            next.delete(String(moduleKey));
            clearedPacedKeysRef.current = next;
            return next;
        });
        syncPacedModuleChange(moduleKey, prevPacedDto);
        handleModalAlert?.("Не удалось снять модуль со сцены.");
    }, [applyDeletedPacedState, syncPacedModuleChange, handleModalAlert, recordSceneHistory]);

    const commitAddCandidate = useCallback((candidate, point, event) => {
        if (!candidate || isPlacingRef.current) return;
        const {row, moduleKey, rowIndex} = candidate;
        const moduleApiId = getCompositionModuleApiId(row);
        if (!moduleApiId || moduleKey == null) return;
        if (placedModulesRef.current.some((inst) => String(inst.moduleKey) === String(moduleKey))) {
            return;
        }

        addingCandidateRef.current = null;
        isPlacingRef.current = true;
        setAddingCandidate(null);
        setIsPlacing(true);

        const finish = async () => {
            try {
                recordSceneHistory();
                const detailData = compositionModulePlacementDetailData(row);
                let snappedPose;
                try {
                    snappedPose = resolvePlacementPose({
                        point,
                        event,
                        snapEnabled: true,
                        cubeSize: compositionSizeM,
                        others: placedModulesRef.current,
                        detailData,
                        pacedPlacement: null,
                        initialAxes: null,
                    });
                } catch (err) {
                    console.warn("composition-3d: placement snap failed", err);
                    snappedPose = resolvePlacementPose({
                        point,
                        event,
                        snapEnabled: false,
                        cubeSize: compositionSizeM,
                        others: placedModulesRef.current,
                        detailData,
                        pacedPlacement: null,
                        initialAxes: null,
                    });
                }

                const tempInstanceId = `composition-temp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
                const inst = buildPlacedCompositionModuleShellFromPose({
                    row,
                    index: rowIndex,
                    localPosition: snappedPose.localPosition,
                    axes: snappedPose.axes || IDENTITY_AXES,
                    faceFlip: Boolean(snappedPose.faceFlip),
                    pacedModule: null,
                    instanceId: tempInstanceId,
                    textureUrlById,
                    compositionTextures: compositionTexturesResolved,
                });
                if (!inst) {
                    throw new Error("Failed to build composition module instance");
                }

                setPlacedModules((prev) => {
                    if (prev.some((item) => String(item.moduleKey) === String(moduleKey))) return prev;
                    const next = [...prev, inst];
                    placedModulesRef.current = next;
                    return next;
                });
                isPlacingRef.current = false;
                setIsPlacing(false);

                setPostPendingModuleKey(moduleKey);
                void createEmbeddedModulePlacement(moduleApiId, {
                    localPosition: snappedPose.localPosition,
                    axes: snappedPose.axes || IDENTITY_AXES,
                    faceFlip: Boolean(snappedPose.faceFlip),
                }).then((pacedDto) => {
                    const resolvedId = pacedDto?.id;
                    if (resolvedId == null || resolvedId === "" || Number(resolvedId) === 0) {
                        throw new Error("POST response has no paced-modules id");
                    }
                    setPacedModuleIdByKey((prev) => ({
                        ...prev,
                        [moduleKey]: resolvedId,
                    }));
                    setClearedPacedKeys((prev) => {
                        if (!prev.has(String(moduleKey))) return prev;
                        const next = new Set(prev);
                        next.delete(String(moduleKey));
                        clearedPacedKeysRef.current = next;
                        return next;
                    });
                    const pacedPose = parsePacedModuleDto(pacedDto);
                    setPlacedModules((prev) => {
                        const next = prev.map((item) => (
                            String(item.moduleKey) === String(moduleKey)
                                ? {
                                    ...item,
                                    instanceId: `composition-paced-${resolvedId}`,
                                    hidden: getPacedDtoHide(pacedDto),
                                    pacedModuleId: resolvedId,
                                    pacedModule: pacedDto,
                                    row: {...item.row, pacedModule: pacedDto},
                                    localPosition: pacedPose?.localPosition ?? item.localPosition,
                                    ...(pacedPose?.axes
                                        ? {
                                            axes: pacedPose.axes,
                                            faceFlip: Boolean(pacedPose.faceFlip),
                                            euler: axesToEulerDegrees(pacedPose.axes),
                                        }
                                        : {}),
                                }
                                : item
                        ));
                        placedModulesRef.current = next;
                        return next;
                    });
                    syncPacedModuleChange(moduleKey, pacedDto);
                    setSelectedInstanceId(`composition-paced-${resolvedId}`);
                }).catch((err) => {
                    console.warn("composition-3d: module POST failed", err);
                    handleModalAlert?.("Нет связи с сервером. Положение не удалось сохранить.");
                    setPlacedModules((prev) => prev.filter(
                        (item) => String(item.moduleKey) !== String(moduleKey),
                    ));
                }).finally(() => {
                    setPostPendingModuleKey(null);
                });
        } catch (err) {
            console.warn("composition-3d: add module failed", err);
                handleModalAlert?.("Не удалось добавить модуль на сцену.");
        } finally {
                isPlacingRef.current = false;
                setIsPlacing(false);
            }
        };

        void finish();
    }, [
        compositionSizeM,
        handleModalAlert,
        syncPacedModuleChange,
        recordSceneHistory,
    ]);

    const addAt = useCallback((point, event) => {
        const candidate = addingCandidateRef.current;
        if (!candidate) return;
        commitAddCandidate(candidate, point, event);
    }, [commitAddCandidate]);

    const startAddFromTable = useCallback((row, moduleKey, rowIndex) => {
        if (!getCompositionModuleApiId(row) || moduleKey == null || isPlacingRef.current) return;
        if (postPendingModuleKey != null) return;
        if (placedModulesRef.current.some((inst) => String(inst.moduleKey) === String(moduleKey))) {
            return;
        }
                setSelectedInstanceId(null);
        const placed = placeModuleOnSceneFromRow(row, moduleKey, rowIndex);
        if (placed) {
            const inst = placedModulesRef.current.find(
                (item) => String(item.moduleKey) === String(moduleKey),
            );
            if (inst) setSelectedInstanceId(inst.instanceId);
            return;
        }
        setAddingCandidate({row, moduleKey, rowIndex});
    }, [placeModuleOnSceneFromRow, postPendingModuleKey]);

    const selectPlacedByKey = useCallback((moduleKey) => {
        const inst = placedModulesRef.current.find(
            (item) => String(item.moduleKey) === String(moduleKey),
        );
        if (inst) setSelectedInstanceId(inst.instanceId);
    }, []);

    useEffect(() => {
        if (!addingMode) return;
        setIsolatedModuleInstanceId(null);
        setSceneContextMenu(null);
    }, [addingMode]);

    useEffect(() => {
        if (isolatedModuleInstanceId && !placedModules.some(
            (inst) => inst.instanceId === isolatedModuleInstanceId,
        )) {
            setIsolatedModuleInstanceId(null);
        }
    }, [placedModules, isolatedModuleInstanceId]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.target?.closest?.("input, textarea, [contenteditable=true]")) return;
            if (event.key === "Escape") {
                setSelectedInstanceId(null);
                setAddingCandidate(null);
                addingCandidateRef.current = null;
                setIsolatedModuleInstanceId(null);
                setSceneContextMenu(null);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    const hasVisiblePlacedModules = useMemo(
        () => placedModules.some((inst) => !inst.hidden),
        [placedModules],
    );
    const hasHiddenPlacedModules = useMemo(
        () => placedModules.some((inst) => inst.hidden),
        [placedModules],
    );
    const sceneMenuInstance = sceneContextMenu
        ? placedModules.find((inst) => inst.instanceId === sceneContextMenu.instanceId) ?? null
        : null;
    const sceneMenuVisiblePeerCount = useMemo(
        () => placedModules.filter((inst) => !inst.hidden).length,
        [placedModules],
    );
    const sceneMenuIsolated = Boolean(isolatedModuleInstanceId);
    const canRevealAllScene = Boolean(isolatedModuleInstanceId) || hasHiddenPlacedModules;

    const instructionText = useMemo(() => {
        if (sceneViewOnlyEffective) {
            if (isPlanarOrthoView(activeCameraView)) {
                return "Просмотр сцены: ЛКМ/ПКМ — панорама, колёсико — зум. Виды — в плашке над сценой. Ширину модуля меняйте в колонке «Габарит».";
            }
            return "Просмотр сцены: ЛКМ — вращение, ПКМ — панорама, колёсико — зум. Виды — в плашке над сценой. Ширину модуля меняйте в колонке «Габарит».";
        }
        if (isPlacing) return "Размещение модуля…";
        if (addingMode) {
            return snapEnabled
                ? "Добавление модуля: клик по полу или стенке у осей."
                : "Добавление модуля: клик по полу или стенке (без привязки).";
        }
        if (isolatedModuleInstanceId) {
            return "Показан только выбранный модуль. ПКМ — «Показать все» или клик по пустому месту.";
        }
        if (selectedInstanceId) {
            return "Двигайте стрелками; поворот — X/Y/Z. Esc — снять выбор. ПКМ по модулю — скрыть остальное.";
        }
        if (isPlanarOrthoView(activeCameraView)) {
            return "Плоский вид: ЛКМ/ПКМ — панорама, колёсико — зум. Виды — в плашке над сценой.";
        }
        return "ЛКМ — вращение, ПКМ — панорама, колёсико — зум. Виды — в плашке над сценой.";
    }, [
        addingMode,
        activeCameraView,
        isPlacing,
        isolatedModuleInstanceId,
        selectedInstanceId,
        snapEnabled,
        sceneViewOnlyEffective,
    ]);

    const compositionCanvasElement = (
        <Box
            ref={setSceneHostNode}
            sx={{
                flex: 1,
                minHeight: 0,
                position: "relative",
                backgroundColor: "#f3f4f6",
                width: "100%",
                height: "100%",
            }}
        >
            <Canvas
                style={{position: "absolute", inset: 0}}
                gl={{antialias: true}}
                dpr={[1, 2]}
                onPointerMissed={(event) => {
                    if (canvasPreviewOnly) {
                        handleCatalogScenePointerMissed(event);
                        return;
                    }
                    if (gizmoDraggingRef.current) return;
                    if (addingMode) return;
                    if (scenePointerRef.current.dragged) return;
                    if (event.button === 2) return;
                    clearSceneIsolation();
                    setSelectedInstanceId(null);
                }}
                onContextMenu={(event) => {
                    event.preventDefault();
                    if (canvasPreviewOnly || sceneViewOnlyEffective) {
                        return;
                    }
                    const instanceId = rmbOnSelectedRef.current;
                    if (instanceId) {
                        openSceneContextMenu(instanceId, event);
                    }
                }}
            >
                <Composition3DScene
                    compositionSizeM={compositionSizeM}
                    activeCameraView={activeCameraView}
                    cameraViewRequest={cameraViewRequest}
                    cameraDistanceScale={
                        canvasPreviewOnly
                            ? CUSTOMER_CATALOG_3D_CAMERA_DISTANCE_SCALE
                            : 2.5
                    }
                    placedModules={placedModules}
                    selectedInstanceId={selectedInstanceId}
                    isolatedModuleInstanceId={isolatedModuleInstanceId}
                    addingMode={addingMode}
                    snapEnabled={snapEnabled}
                    orthoEnabled={orthoEnabled}
                    showShellTransparent={showShellTransparent}
                    showCompositionShell={showCompositionShell}
                    showModuleShells={showModuleShells}
                    sceneViewOnly={sceneViewOnlyEffective}
                    hoveredModuleRowId={hoveredModuleRowId}
                    hoverHighlightRowIds={hoverHighlightRowIds}
                    compactPriorityMarker={canvasPreviewOnly}
                    customerListSceneInteraction={customerListSceneInteraction}
                    onCatalogModuleListHoverStart={handleCatalogModuleListHoverStart}
                    onCatalogModuleListHoverEnd={handleCatalogModuleListHoverEnd}
                    onCatalogModuleListClick={handleCatalogModuleListClick}
                    postPendingModuleKey={postPendingModuleKey}
                    gizmoDraggingRef={gizmoDraggingRef}
                    onAddAt={addAt}
                    onSelectInstance={setSelectedInstanceId}
                    onTransformEnd={commitModulePose}
                    onSelectedRightPointerDown={markSelectedRightPointerDown}
                    onSceneContextMenu={openSceneContextMenu}
                />
            </Canvas>
        </Box>
    );

    if (canvasPreviewOnly) {
        const showCustomer3dLoading = customer3dEnabled && customer3dLoading;
        const showCustomer3dCanvas = !customer3dEnabled || customer3dComposition != null;
        return (
            <Box
                sx={[
                    {
                        width: "100%",
                        height: "100%",
                        minHeight: 280,
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                    },
                    previewContainerSx,
                ]}
            >
                {showCustomer3dLoading ? (
                    <Box sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1,
                        bgcolor: (theme) => theme.palette.background.grey,
                    }}>
                        <CircularProgress disableShrink/>
                    </Box>
                ) : null}
                {showCustomer3dCanvas ? (
                    <>
                        <Module3DViewBar
                            activeCameraView={activeCameraView}
                            onCameraViewChange={applyCameraView}
                            disabled={customer3dLoading}
                            showHistoryActions={false}
                            showViewHint={false}
                            overlayReserveRightPx={CATALOG_MODULES_VIEW_TOGGLE_OVERLAY_RESERVE_PX}
                        />
                        <Box sx={{flex: 1, minHeight: 0, position: "relative"}}>
                            {compositionCanvasElement}
                        </Box>
                    </>
                ) : null}
            </Box>
        );
    }

    const sceneBlockMinHeight = embeddedLayout ? "min(560px, 70vh)" : "calc(100vh - 180px)";
    const sidebarMaxHeight = embeddedLayout ? "min(560px, 70vh)" : "calc(100vh - 180px)";

    return (
        <Box sx={{
            display: "flex",
            gap: 2,
            alignItems: "stretch",
            paddingBottom: embeddedLayout ? 0 : "30px",
            width: "100%",
            minWidth: 0,
        }}>
            <Box
                sx={{
                    width: COMPOSITION3D_SIDEBAR_WIDTH_PX,
                    flexShrink: 0,
                    maxHeight: sidebarMaxHeight,
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
                    padding: 1.5,
                    overflowX: "hidden",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <Typography sx={{fontWeight: 700}}>
                    Модули композиции
                </Typography>
                <Typography sx={{fontSize: "0.75rem", color: "text.secondary"}}>
                    {sceneViewOnlyEffective
                        ? "3D-вид кухни: можно вращать камеру и приближать сцену. Расстановку модулей меняет организация; здесь доступна только смена ширины в таблице."
                        : "Зелёный куб — габариты композиции. Сохранённые модули подгружаются при открытии; «+» без позы на бэке — клик по сцене, с сохранённой позой — сразу на сцену."}
                    {compositionId != null ? ` Композиция #${compositionId}.` : ""}
                </Typography>
                <Typography color="text.secondary" sx={{fontSize: 13}}>
                    {instructionText}
                </Typography>

                <TableContainer
                        sx={{
                        overflow: "visible",
                            width: "100%",
                        minWidth: 0,
                        alignSelf: "stretch",
                    }}
                >
                    <Table
                        size="medium"
                        stickyHeader
                        sx={COMPOSITION3D_TABLE_SX}
                    >
                        <colgroup>
                            {!sceneViewOnlyEffective ? <col style={{width: 40}}/> : null}
                            <col style={{width: 40}}/>
                            <col/>
                            <col style={{width: COMPOSITION3D_SIZE_COLUMN_WIDTH_PX}}/>
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                {!sceneViewOnlyEffective ? (
                                    <TableCell sx={{width: 40, px: "4px !important"}}> </TableCell>
                                ) : null}
                                <TableCell
                                    sx={{
                                        width: 40,
                                        maxWidth: 40,
                                        minWidth: 40,
                                        px: "4px !important",
                                        textAlign: "center",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        boxSizing: "border-box",
                                    }}
                                    title="Приоритет"
                                >
                                    Приоритет
                                </TableCell>
                                <TableCell>Название</TableCell>
                                <TableCell sx={{
                                    width: COMPOSITION3D_SIZE_COLUMN_WIDTH_PX,
                                    maxWidth: COMPOSITION3D_SIZE_COLUMN_WIDTH_PX,
                                    whiteSpace: "nowrap",
                                }}>
                                    Габарит
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {moduleRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={sceneViewOnlyEffective ? 3 : 4} sx={{border: "none", py: 2}}>
                                        <Typography color="text.secondary" sx={{fontSize: 13}}>
                                        В композиции пока нет модулей.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : moduleRows.map((row, rowIndex) => {
                                const moduleKey = getCompositionModuleKey(row, rowIndex);
                                const moduleKeyStr = String(moduleKey);
                                const onScene = placedByKey.has(moduleKeyStr);
                                const hasSavedPlacement = !clearedPacedKeys.has(moduleKeyStr)
                                    && (
                                        isCompositionModulePlacedDto(row)
                                        || getPacedModuleId(row) != null
                                    );
                                const loadingOntoScene = sceneHydrating && hasSavedPlacement && !onScene;
                                const savingOntoScene = postPendingModuleKey === moduleKey;
                                const placedInst = placedModules.find(
                                    (item) => String(item.moduleKey) === moduleKeyStr,
                                );
                                const childrenPending = onScene
                                    && placedInst
                                    && !placedInst.childrenHydrated;
                                const isHiddenOnScene = onScene && Boolean(
                                    placedInst?.hidden ?? getPacedDtoHide(row?.pacedModule),
                                );
                                const isLoadingOntoScene = loadingOntoScene
                                    || savingOntoScene
                                    || childrenPending;
                                const showMinus = onScene && !isLoadingOntoScene;
                                const rowContentFaded = isHiddenOnScene && onScene;
                                const label = row?.name ?? row?.module?.name ?? "-";
                                const modulePagePath = buildModuleCompositionPagePath(row);
                                const modulePageLink = modulePagePath
                                    ? withCompositionIdQuery(modulePagePath, compositionId, compositionName)
                                    : null;
                                const rowMenuTooltip = isLoadingOntoScene
                                    ? "Загружается на сцену"
                                    : "Действия";

                                return (
                                    <TableRow
                                        key={moduleKeyStr}
                                        onClick={() => {
                                            if (sceneViewOnlyEffective || !onScene || isLoadingOntoScene) return;
                                            selectPlacedByKey(moduleKey);
                                        }}
                                        sx={(theme) => ({
                                            height: "36px",
                                            cursor: !sceneViewOnlyEffective && onScene && !isLoadingOntoScene ? "pointer" : "default",
                                            backgroundColor:
                                                placedInst?.instanceId === selectedInstanceId
                                                ? theme.palette.primary.light
                                                : "white",
                                            "&:hover": {
                                                backgroundColor:
                                                    placedInst?.instanceId === selectedInstanceId
                                                        ? theme.palette.primary.light50
                                                        : theme.palette.primary.stroke,
                                            },
                                            "&:nth-of-type(even)": {
                                                backgroundColor:
                                                    placedInst?.instanceId === selectedInstanceId
                                                    ? theme.palette.primary.light
                                                    : theme.palette.primary.lightGrey,
                                            },
                                        })}
                                    >
                                        {!sceneViewOnlyEffective ? (
                                        <TableCell sx={{px: "4px !important", width: 40}}>
                                            <Tooltip title={rowMenuTooltip} arrow placement="top">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={
                                                            addingMode
                                                            || isPlacing
                                                            || isLoadingOntoScene
                                                            || !onScene
                                                        }
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            openModuleRowMenu(event, row, moduleKey);
                                                        }}
                                                        aria-label="Действия"
                                                        sx={{p: 0.25}}
                                                    >
                                                        <MoreHorizIcon fontSize="small"/>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        ) : null}
                                        <TableCell
                                            sx={{
                                                px: "4px !important",
                                                width: 40,
                                                maxWidth: 40,
                                                minWidth: 40,
                                                textAlign: "center",
                                                overflow: "hidden",
                                                boxSizing: "border-box",
                                                opacity: rowContentFaded ? 0.5 : 1,
                                            }}
                                        >
                                            {row?.priority ?? 0}
                                        </TableCell>
                                        <TableCell sx={{width: "34%", overflow: "hidden", maxWidth: 0}}>
                                            {modulePageLink ? (
                                                <TableTextLink
                                                    to={modulePageLink}
                                                    title={label}
                                                    onClick={() => EditLocalStoragePagesData(modulePagePath, "tab", 1)}
                                                    sx={TABLE_TEXT_LINK_INLINE_SX}
                                                >
                                                    {label}
                                                </TableTextLink>
                                            ) : (
                                                <Box
                                                    component="span"
                                                    title={label}
                                            sx={{
                                                        display: "block",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                                    {label}
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{
                                            width: COMPOSITION3D_SIZE_COLUMN_WIDTH_PX,
                                            maxWidth: COMPOSITION3D_SIZE_COLUMN_WIDTH_PX,
                                            whiteSpace: "nowrap",
                                        }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    gap: 1,
                                                    paddingRight: "10px",
                                                    minHeight: "30px",
                                                    width: "100%",
                                                    boxSizing: "border-box",
                                                    minWidth: 0,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        overflow: "hidden",
                                                        fontSize: "inherit",
                                                    }}
                                                >
                                                    <Composition3DModuleSizeCell
                                                        row={row}
                                                        faded={rowContentFaded}
                                                        useCustomerModuleWidthLockRules={
                                                            useCustomerModuleWidthLockRules
                                                        }
                                                        disabled={
                                                            widthSaving
                                                            || addingMode
                                                            || isPlacing
                                                            || isLoadingOntoScene
                                                        }
                                                        onWidthClick={openModuleWidthEditDialog}
                                                    />
                                                </Box>
                                                {!sceneViewOnlyEffective ? (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <TableVisibilityButton
                                                        hidden={isHiddenOnScene}
                                                        disabled={
                                                            isLoadingOntoScene
                                                            || !onScene
                                                            || addingMode
                                                            || isPlacing
                                                        }
                                                        tooltip={
                                                            isLoadingOntoScene
                                                                ? "Загружается на сцену"
                                                                : (!onScene ? "Модуль ещё не на сцене" : undefined)
                                                        }
                                                        onClick={() => {
                                                            void toggleModuleVisibility(
                                                                moduleKey,
                                                                row,
                                                                !isHiddenOnScene,
                                                            );
                                                        }}
                                                    />
                                                    <TableAddButton
                                                        showMinus={showMinus}
                                                        muted={isLoadingOntoScene || isHiddenOnScene}
                                                        disabled={
                                                            isLoadingOntoScene
                                                            || addingMode
                                                            || isPlacing
                                                            || isHiddenOnScene
                                                            || (!showMinus && onScene)
                                                        }
                                                        tooltip={
                                                            isLoadingOntoScene
                                                                ? "Загружается на сцену"
                                                                : (isHiddenOnScene
                                                                    ? "Сначала покажите модуль на сцене"
                                                                    : (showMinus ? "Убрать" : "Добавить"))
                                                        }
                                                        ariaLabel={showMinus ? "Убрать со сцены" : "Добавить на сцену"}
                                                        onClick={() => {
                                                            if (showMinus) {
                                                                void removeModuleByKey(moduleKey, row);
                                                                return;
                                                            }
                                                            startAddFromTable(row, moduleKey, rowIndex);
                                                        }}
                                                    />
                                                </Box>
                                                ) : null}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {!sceneViewOnlyEffective ? (
                <Menu
                    anchorEl={moduleRowMenu?.anchorEl}
                    open={Boolean(moduleRowMenu)}
                    onClose={closeModuleRowMenu}
                    anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                    transformOrigin={{vertical: "top", horizontal: "left"}}
                >
                    {moduleRowMenu && resolveModulePacedId(
                        moduleRowMenu.moduleKey,
                        moduleRowMenu.row,
                    ) != null ? (
                        <>
                            <MenuItem
                                disabled={addingMode || isPlacing}
                                onClick={() => {
                                    const moduleKey = moduleRowMenu.moduleKey;
                                    const row = moduleRowMenu.row;
                                    const inst = placedModules.find(
                                        (item) => String(item.moduleKey) === String(moduleKey),
                                    );
                                    const isHidden = Boolean(
                                        inst?.hidden ?? getPacedDtoHide(row?.pacedModule),
                                    );
                                    void toggleModuleVisibility(moduleKey, row, !isHidden);
                                    closeModuleRowMenu();
                                }}
                            >
                                {(() => {
                                    const inst = placedModules.find(
                                        (item) => String(item.moduleKey) === String(moduleRowMenu.moduleKey),
                                    );
                                    const isHidden = Boolean(
                                        inst?.hidden ?? getPacedDtoHide(moduleRowMenu.row?.pacedModule),
                                    );
                                    return isHidden ? "Показать на сцене" : "Скрыть на сцене";
                                })()}
                            </MenuItem>
                            <MenuItem
                                disabled={addingMode || isPlacing || !hasVisiblePlacedModules}
                                onClick={() => {
                                    void setAllModulesVisibility(true);
                                    closeModuleRowMenu();
                                }}
                            >
                                Скрыть все
                            </MenuItem>
                            <MenuItem
                                disabled={addingMode || isPlacing || !hasHiddenPlacedModules}
                                onClick={() => {
                                    void setAllModulesVisibility(false);
                                    closeModuleRowMenu();
                                }}
                            >
                                Показать все
                            </MenuItem>
                            <MenuItem
                                disabled={
                                    addingMode
                                    || isPlacing
                                    || !hasVisiblePlacedModules
                                    || (() => {
                                        const inst = placedModules.find(
                                            (item) => String(item.moduleKey) === String(moduleRowMenu.moduleKey),
                                        );
                                        return Boolean(
                                            inst?.hidden ?? getPacedDtoHide(moduleRowMenu.row?.pacedModule),
                                        );
                                    })()
                                }
                                onClick={() => {
                                    void setAllModulesVisibility(true, moduleRowMenu.moduleKey);
                                    closeModuleRowMenu();
                                }}
                            >
                                Скрыть кроме текущего
                            </MenuItem>
                        </>
                    ) : null}
                </Menu>
                ) : null}

                {!sceneViewOnlyEffective ? (
                <Module3DToolbar
                    hasSelection={Boolean(selectedInstanceId)}
                    disabled={addingMode || isPlacing || Boolean(postPendingModuleKey)}
                    onRotate90={rotateSelectedBy90}
                    onMirror={mirrorSelected}
                    basePointMm={basePointMm}
                    onBasePointCommit={commitBasePointMm}
                    snapEnabled={snapEnabled}
                    onSnapEnabledChange={setSnapEnabled}
                    orthoEnabled={orthoEnabled}
                    onOrthoEnabledChange={setOrthoEnabled}
                    showShellTransparent={showShellTransparent}
                    onShowShellTransparentChange={setShowShellTransparent}
                    showCompositionShell={showCompositionShell}
                    onShowCompositionShellChange={setShowCompositionShell}
                    showModuleShells={showModuleShells}
                    onShowModuleShellsChange={setShowModuleShells}
                    showCompositionShellToggle
                    showModuleShellsToggle
                    showMachiningToggles={false}
                    showReferenceCubeToggle={false}
                    showShellTransparentToggle={false}
                    showPriorityMarker3dCoordinates
                    priorityMarker3dCoordX={priorityCoordDraft.x}
                    priorityMarker3dCoordY={priorityCoordDraft.y}
                    priorityMarker3dCoordLimits={priorityCoordLimits}
                    priorityMarker3dCoordsDisabled={priorityCoordInputsDisabled}
                    onPriorityMarker3dCoordXChange={(value) => {
                        priorityCoordEditedAxesRef.current.add("x");
                        setPriorityCoordDraft((prev) => ({...prev, x: value}));
                    }}
                    onPriorityMarker3dCoordYChange={(value) => {
                        priorityCoordEditedAxesRef.current.add("y");
                        setPriorityCoordDraft((prev) => ({...prev, y: value}));
                    }}
                    onPriorityMarker3dCoordBlur={handlePriorityCoordinateBlur}
                    onPriorityMarker3dCoordCommit={handlePriorityCoordinateCommit}
                    onPriorityMarker3dCoordKeyDown={handlePriorityCoordinateKeyDown}
                    selectionHint="Выберите модуль на сцене, чтобы менять координаты, поворот и зеркало."
                />
                ) : null}
            </Box>

            <Box
                sx={{
                    flex: "1 1 0",
                    minHeight: sceneBlockMinHeight,
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 320,
                    maxWidth: `calc(100% - ${COMPOSITION3D_SIDEBAR_WIDTH_PX}px - 16px)`,
                }}
            >
                <Module3DViewBar
                    activeCameraView={activeCameraView}
                    onCameraViewChange={applyCameraView}
                    disabled={addingMode || isPlacing || Boolean(postPendingModuleKey)}
                    canUndo={sceneHistory.past.length > 0}
                    canRedo={sceneHistory.future.length > 0}
                    onUndo={undoSceneChange}
                    onRedo={redoSceneChange}
                    showHistoryActions={!sceneViewOnlyEffective}
                />
                {addingMode && (
                    <Box sx={{
                        px: 2,
                        py: 0.75,
                        backgroundColor: "primary.light",
                        fontSize: "0.8rem",
                    }}>
                        Кликните по сцене, чтобы разместить модуль «
                        {addingCandidate?.row?.name ?? addingCandidate?.row?.module?.name ?? ""}
                        »
                    </Box>
                )}
                {compositionCanvasElement}

                {!sceneViewOnlyEffective ? (
                <Menu
                    open={Boolean(sceneContextMenu)}
                    onClose={() => setSceneContextMenu(null)}
                    anchorReference="anchorPosition"
                    anchorPosition={
                        sceneContextMenu
                            ? {top: sceneContextMenu.mouseY, left: sceneContextMenu.mouseX}
                            : undefined
                    }
                >
                    <MenuItem
                        disabled={
                            addingMode
                            || isPlacing
                            || sceneMenuIsolated
                            || sceneMenuVisiblePeerCount < 2
                        }
                        onClick={() => {
                            if (!sceneContextMenu?.instanceId) return;
                            setIsolatedModuleInstanceId(sceneContextMenu.instanceId);
                            setSceneContextMenu(null);
                        }}
                    >
                        Скрыть остальное
                    </MenuItem>
                    <MenuItem
                        disabled={
                            addingMode
                            || isPlacing
                            || !sceneContextMenu?.instanceId
                            || Boolean(sceneMenuInstance?.hidden)
                        }
                        onClick={() => {
                            hideSceneModule(sceneContextMenu?.instanceId);
                        }}
                    >
                        Скрыть выделенное
                    </MenuItem>
                    <MenuItem
                        disabled={addingMode || isPlacing || !canRevealAllScene}
                        onClick={revealAllModules}
                    >
                        Показать все
                    </MenuItem>
                </Menu>
                ) : null}
            </Box>
            <Dialog
                open={Boolean(widthEditDialog)}
                onClose={closeModuleWidthEditDialog}
            >
                <DialogTitle sx={{textAlign: "center"}}>
                    Изменение ширины
                </DialogTitle>
                <Box sx={{px: "1.25rem", pb: 1}}>
                    <Typography sx={{mb: 1.5, fontSize: "14px", color: "text.secondary"}}>
                        {widthEditDialog?.row
                            ? getModuleCompositionDisplayName(widthEditDialog.row)
                            : ""}
                    </Typography>
                    <TextField
                        type="number"
                        size="small"
                        fullWidth
                        autoFocus
                        label="Ширина, мм"
                        value={widthEditDialog?.draftWidth ?? ""}
                        disabled={widthSaving}
                        onChange={(event) => {
                            const draftWidth = event.target.value;
                            setWidthEditDialog((prev) => (
                                prev ? {...prev, draftWidth} : prev
                            ));
                        }}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                event.preventDefault();
                                submitModuleWidthEditDialog();
                            }
                        }}
                    />
                </Box>
                <DialogActions sx={{p: "1.25rem"}}>
                    <ButtonExit
                        text="Отмена"
                        onClick={closeModuleWidthEditDialog}
                    />
                    <ButtonAdd
                        text="Далее"
                        loading={widthSaving}
                        onClick={submitModuleWidthEditDialog}
                    />
                </DialogActions>
            </Dialog>
            <Dialog
                open={Boolean(widthConfirm)}
                onClose={cancelModuleWidthConfirm}
            >
                <DialogTitle sx={{textAlign: "center"}}>
                    Вы подтверждаете изменение?
                </DialogTitle>
                {widthConfirmContext ? (
                    <ModuleCompositionWidthConfirmDialogContent
                        compositionName={widthConfirmContext.compositionName}
                        moduleName={widthConfirmContext.moduleName}
                        previousValue={widthConfirmContext.previousValue}
                        nextValue={widthConfirmContext.nextValue}
                    />
                ) : null}
                <DialogActions sx={{p: "1.25rem"}}>
                    <ButtonExit
                        text="Нет"
                        onClick={cancelModuleWidthConfirm}
                    />
                    <ButtonAdd
                        text="Да"
                        loading={widthSaving}
                        onClick={submitModuleWidthConfirm}
                    />
                </DialogActions>
            </Dialog>
        </Box>
    );
};

/** Заказчик: TOP + blockSize; по умолчанию только просмотр сцены (без зелёных кубов и расстановки). */
export function CustomerComposition3DView({sceneViewOnly = true, embeddedLayout = false, ...props}) {
    return (
        <Composition3DView
            useCustomerModuleWidthLockRules
            sceneViewOnly={sceneViewOnly}
            embeddedLayout={embeddedLayout}
            {...props}
        />
    );
}

/** Вместо 2D-картинки на вкладке «Список шкафов»: только canvas, orbit/zoom, без UI 3D-редактора. */
export function CustomerComposition3DCatalogPreview(props) {
    return (
        <Composition3DView
            useCustomerModuleWidthLockRules
            canvasPreviewOnly
            sceneViewOnly
            {...props}
        />
    );
}

export default Composition3DView;
